/**-----------------------------------------------------------------------------------------
* Copyright © 2026 Progress Software Corporation. All rights reserved.
* Licensed under commercial license. See LICENSE.md in the project root for more information
*-------------------------------------------------------------------------------------------*/
import * as i0 from '@angular/core';
import { TemplateRef, EventEmitter, HostBinding, Output, Input, Component, InjectionToken, Inject, Optional, Injectable, Directive, forwardRef, ViewChild, ViewChildren, ContentChildren, isDevMode, Renderer2, Host, HostListener, ContentChild, NgModule } from '@angular/core';
import { NgClass, NgTemplateOutlet, NgStyle } from '@angular/common';
import * as i2 from '@angular/animations';
import { style, animate, keyframes, trigger, state, transition } from '@angular/animations';
import * as i1 from '@progress/kendo-angular-buttons';
import { KENDO_BUTTON, ButtonComponent, Button } from '@progress/kendo-angular-buttons';
import * as i1$1 from '@progress/kendo-angular-l10n';
import { LocalizationService, L10N_PREFIX, RTL, ComponentMessages } from '@progress/kendo-angular-l10n';
import { take, delay, takeUntil, filter, map, share, tap, switchMap } from 'rxjs/operators';
import { xIcon, windowRestoreIcon, windowIcon, windowMinimizeIcon } from '@progress/kendo-svg-icons';
import { validatePackage } from '@progress/kendo-licensing';
import { of, Subscription, Subject, merge } from 'rxjs';
import * as i1$2 from '@progress/kendo-angular-common';
import { setHTMLAttributes, getLicenseMessage, shouldShowValidationUI, isDocumentAvailable, normalizeKeys, Keys, focusableSelector, WatermarkOverlayComponent, KENDO_WEBMCP_HOST, DraggableDirective, isChanged } from '@progress/kendo-angular-common';
import { offset, getWindowViewPort } from '@progress/kendo-popup-common';
import { IconWrapperComponent, IconsService } from '@progress/kendo-angular-icons';

/**
 * Represents the action buttons of the Dialog.
 * ([See example.](https://www.telerik.com/kendo-angular-ui/components/dialogs/dialog/action-buttons))
 *
 * ```html
 * <kendo-dialog>
 *  <kendo-dialog-actions>
 *     <button kendoButton fillMode="flat" themeColor="primary" (click)="onPrimaryActionClick()">
 *       Primary Action
 *    </button>
 *  </kendo-dialog-actions>
 * </kendo-dialog>
 * ```
 */
const _c0 = ["*"];
function DialogActionsComponent_Conditional_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵprojection(0);
  }
}
function DialogActionsComponent_Conditional_1_For_1_Conditional_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "span", 1);
  }
}
function DialogActionsComponent_Conditional_1_For_1_Conditional_1_Template(rf, ctx) {
  if (rf & 1) {
    const _r1 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "button", 3);
    i0.ɵɵlistener("click", function DialogActionsComponent_Conditional_1_For_1_Conditional_1_Template_button_click_0_listener($event) {
      i0.ɵɵrestoreView(_r1);
      const action_r2 = i0.ɵɵnextContext().$implicit;
      const ctx_r2 = i0.ɵɵnextContext(2);
      return i0.ɵɵresetView(ctx_r2.onButtonClick(action_r2, $event));
    });
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const action_r2 = i0.ɵɵnextContext().$implicit;
    i0.ɵɵproperty("disabled", action_r2.disabled)("fillMode", action_r2.fillMode)("themeColor", action_r2.themeColor)("ngClass", action_r2.cssClass)("svgIcon", action_r2.svgIcon)("icon", action_r2.icon);
    i0.ɵɵattribute("aria-label", action_r2.text);
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate1(" ", action_r2.text, " ");
  }
}
function DialogActionsComponent_Conditional_1_For_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵconditionalCreate(0, DialogActionsComponent_Conditional_1_For_1_Conditional_0_Template, 1, 0, "span", 1)(1, DialogActionsComponent_Conditional_1_For_1_Conditional_1_Template, 2, 8, "button", 2);
  }
  if (rf & 2) {
    const action_r2 = ctx.$implicit;
    const ctx_r2 = i0.ɵɵnextContext(2);
    i0.ɵɵconditional(ctx_r2.isDivider(action_r2) ? 0 : 1);
  }
}
function DialogActionsComponent_Conditional_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵrepeaterCreate(0, DialogActionsComponent_Conditional_1_For_1_Template, 2, 1, null, null, i0.ɵɵrepeaterTrackByIdentity);
  }
  if (rf & 2) {
    const ctx_r2 = i0.ɵɵnextContext();
    i0.ɵɵrepeater(ctx_r2.actionsArray);
  }
}
function DialogActionsComponent_Conditional_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainer(0, 0);
  }
  if (rf & 2) {
    const ctx_r2 = i0.ɵɵnextContext();
    i0.ɵɵproperty("ngTemplateOutlet", ctx_r2.actionsTemplate);
  }
}
function DialogTitleBarComponent_Conditional_3_Template(rf, ctx) {
  if (rf & 1) {
    const _r1 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "div", 2)(1, "button", 3);
    i0.ɵɵlistener("click", function DialogTitleBarComponent_Conditional_3_Template_button_click_1_listener($event) {
      i0.ɵɵrestoreView(_r1);
      const ctx_r1 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r1.onCloseClick($event));
    });
    i0.ɵɵelementEnd()();
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵadvance();
    i0.ɵɵproperty("svgIcon", ctx_r1.xIcon);
    i0.ɵɵattribute("title", ctx_r1.closeButtonTitle)("aria-label", ctx_r1.closeButtonTitle);
  }
}
const _c1 = ["dialog"];
const _c2 = [[["kendo-dialog-titlebar"]], "*", [["kendo-dialog-actions"]]];
const _c3 = ["kendo-dialog-titlebar", "*", "kendo-dialog-actions"];
function DialogComponent_Conditional_4_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "kendo-dialog-titlebar", 4);
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵproperty("closable", ctx_r0.closable)("closeTitle", ctx_r0.closeTitle)("id", ctx_r0.titleId);
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate(ctx_r0.title);
  }
}
function DialogComponent_Conditional_5_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵprojection(0);
  }
}
function DialogComponent_Conditional_7_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵprojection(0, 1);
  }
}
function DialogComponent_Conditional_8_ng_template_0_Template(rf, ctx) {}
function DialogComponent_Conditional_8_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵtemplate(0, DialogComponent_Conditional_8_ng_template_0_Template, 0, 0, "ng-template", 6);
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵproperty("ngTemplateOutlet", ctx_r0.contentTemplate);
  }
}
function DialogComponent_Conditional_9_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵprojection(0, 2);
  }
}
function DialogComponent_Conditional_10_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "kendo-dialog-actions", 7);
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵproperty("actions", ctx_r0.actions)("layout", ctx_r0.actionsLayout);
  }
}
function DialogComponent_Conditional_11_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "div", 8);
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵproperty("licenseMessage", ctx_r0.licenseMessage);
  }
}
const _c4 = a0 => ({
  "$implicit": a0
});
function WindowTitleBarComponent_Conditional_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵprojection(0);
  }
}
function WindowTitleBarComponent_Conditional_1_ng_template_0_Template(rf, ctx) {}
function WindowTitleBarComponent_Conditional_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵtemplate(0, WindowTitleBarComponent_Conditional_1_ng_template_0_Template, 0, 0, "ng-template", 0);
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵproperty("ngTemplateOutlet", ctx_r0.template)("ngTemplateOutletContext", i0.ɵɵpureFunction1(2, _c4, ctx_r0.service));
  }
}
const _c5 = ["kendoWindowCloseAction", ""];
function WindowCloseActionDirective_Conditional_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "kendo-icon-wrapper", 0);
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵproperty("svgIcon", ctx_r0.xIcon);
  }
}
function WindowCloseActionDirective_Conditional_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "span", 1);
    i0.ɵɵelement(1, "img", 4);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance();
    i0.ɵɵproperty("src", ctx_r0.imageUrl, i0.ɵɵsanitizeUrl);
  }
}
function WindowCloseActionDirective_Conditional_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "span", 2);
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵproperty("ngClass", ctx_r0.iconClass);
  }
}
const _c6 = ["kendoWindowRestoreAction", ""];
function WindowRestoreActionDirective_Conditional_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "kendo-icon-wrapper", 0);
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵproperty("svgIcon", ctx_r0.windowRestoreIcon);
  }
}
function WindowRestoreActionDirective_Conditional_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "span", 1);
    i0.ɵɵelement(1, "img", 4);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance();
    i0.ɵɵproperty("src", ctx_r0.imageUrl, i0.ɵɵsanitizeUrl);
  }
}
function WindowRestoreActionDirective_Conditional_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "span", 2);
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵproperty("ngClass", ctx_r0.iconClass);
  }
}
const _c7 = ["kendoWindowMaximizeAction", ""];
function WindowMaximizeActionDirective_Conditional_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "kendo-icon-wrapper", 0);
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵproperty("svgIcon", ctx_r0.windowIcon);
  }
}
function WindowMaximizeActionDirective_Conditional_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "span", 1);
    i0.ɵɵelement(1, "img", 4);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance();
    i0.ɵɵproperty("src", ctx_r0.imageUrl, i0.ɵɵsanitizeUrl);
  }
}
function WindowMaximizeActionDirective_Conditional_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "span", 2);
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵproperty("ngClass", ctx_r0.iconClass);
  }
}
const _c8 = ["kendoWindowMinimizeAction", ""];
function WindowMinimizeActionDirective_Conditional_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "kendo-icon-wrapper", 0);
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵproperty("svgIcon", ctx_r0.windowMinimizeIcon);
  }
}
function WindowMinimizeActionDirective_Conditional_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "span", 1);
    i0.ɵɵelement(1, "img", 4);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance();
    i0.ɵɵproperty("src", ctx_r0.imageUrl, i0.ɵɵsanitizeUrl);
  }
}
function WindowMinimizeActionDirective_Conditional_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "span", 2);
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵproperty("ngClass", ctx_r0.iconClass);
  }
}
const _c9 = [[["kendo-window-titlebar"]], "*"];
const _c10 = ["kendo-window-titlebar", "*"];
function WindowComponent_Conditional_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "kendo-window-titlebar", 1)(1, "span", 4);
    i0.ɵɵtext(2);
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(3, "div", 5);
    i0.ɵɵelement(4, "button", 6)(5, "button", 7)(6, "button", 8)(7, "button", 9);
    i0.ɵɵelementEnd()();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵproperty("template", ctx_r0.titleBarTemplate)("id", ctx_r0.titleId);
    i0.ɵɵadvance(2);
    i0.ɵɵtextInterpolate(ctx_r0.title);
    i0.ɵɵadvance(2);
    i0.ɵɵattribute("title", ctx_r0.minimizeButtonTitle)("aria-label", ctx_r0.minimizeButtonTitle);
    i0.ɵɵadvance();
    i0.ɵɵattribute("title", ctx_r0.maximizeButtonTitle)("aria-label", ctx_r0.maximizeButtonTitle);
    i0.ɵɵadvance();
    i0.ɵɵattribute("title", ctx_r0.restoreButtonTitle)("aria-label", ctx_r0.restoreButtonTitle);
    i0.ɵɵadvance();
    i0.ɵɵattribute("title", ctx_r0.closeButtonTitle)("aria-label", ctx_r0.closeButtonTitle);
  }
}
function WindowComponent_Conditional_3_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵprojection(0);
  }
}
function WindowComponent_Conditional_4_Conditional_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵprojection(0, 1);
  }
}
function WindowComponent_Conditional_4_Conditional_2_ng_template_0_Template(rf, ctx) {}
function WindowComponent_Conditional_4_Conditional_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵtemplate(0, WindowComponent_Conditional_4_Conditional_2_ng_template_0_Template, 0, 0, "ng-template", 10);
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext(2);
    i0.ɵɵproperty("ngTemplateOutlet", ctx_r0.contentTemplate);
  }
}
function WindowComponent_Conditional_4_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 2);
    i0.ɵɵconditionalCreate(1, WindowComponent_Conditional_4_Conditional_1_Template, 1, 0);
    i0.ɵɵconditionalCreate(2, WindowComponent_Conditional_4_Conditional_2_Template, 1, 1, null, 10);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵproperty("hidden", ctx_r0.state === "minimized" && ctx_r0.keepContent);
    i0.ɵɵadvance();
    i0.ɵɵconditional(!ctx_r0.contentTemplate ? 1 : -1);
    i0.ɵɵadvance();
    i0.ɵɵconditional(ctx_r0.contentTemplate ? 2 : -1);
  }
}
function WindowComponent_Conditional_5_For_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "div", 11);
  }
  if (rf & 2) {
    const dir_r2 = ctx.$implicit;
    i0.ɵɵproperty("direction", dir_r2);
  }
}
function WindowComponent_Conditional_5_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵrepeaterCreate(0, WindowComponent_Conditional_5_For_1_Template, 1, 1, "div", 11, i0.ɵɵrepeaterTrackByIdentity);
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵrepeater(ctx_r0.resizeDirections);
  }
}
function WindowComponent_Conditional_6_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "div", 3);
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵproperty("licenseMessage", ctx_r0.licenseMessage);
  }
}
class DialogActionsComponent {
  el;
  /**
   * Allows the declarative specification of the Dialog `actions`.
   */
  set actions(value) {
    if (value instanceof TemplateRef) {
      this.actionsTemplate = value;
    } else if (Array.isArray(value)) {
      this.actionsArray = value;
    } else {
      throw new Error('"actions" must be either TemplateRef or DialogAction[] instance.');
    }
  }
  /**
   * @hidden
   */
  actionsArray;
  /**
   * @hidden
   */
  actionsTemplate;
  /**
   * Sets the possible layout of the action buttons.
   * @default 'stretched'
   */
  layout = 'stretched';
  /**
   * Fires when the user clicks an action button.
   */
  action = new EventEmitter();
  hostClasses = true;
  get startClassName() {
    return this.layout === 'start';
  }
  get centerClassName() {
    return this.layout === 'center';
  }
  get endClassName() {
    return this.layout === 'end';
  }
  get stretchedClassName() {
    return this.layout === 'stretched';
  }
  constructor(el) {
    this.el = el;
  }
  /**
   * @hidden
   */
  onButtonClick(action, _e) {
    this.action.emit(action);
  }
  /**
   * @hidden
   */
  isDivider(action) {
    return action === 'spacer';
  }
  static ɵfac = function DialogActionsComponent_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DialogActionsComponent)(i0.ɵɵdirectiveInject(i0.ElementRef));
  };
  static ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
    type: DialogActionsComponent,
    selectors: [["kendo-dialog-actions"]],
    hostVars: 16,
    hostBindings: function DialogActionsComponent_HostBindings(rf, ctx) {
      if (rf & 2) {
        i0.ɵɵclassProp("k-actions", ctx.hostClasses)("k-actions-horizontal", ctx.hostClasses)("k-window-actions", ctx.hostClasses)("k-dialog-actions", ctx.hostClasses)("k-actions-start", ctx.startClassName)("k-actions-center", ctx.centerClassName)("k-actions-end", ctx.endClassName)("k-actions-stretched", ctx.stretchedClassName);
      }
    },
    inputs: {
      actions: "actions",
      layout: "layout"
    },
    outputs: {
      action: "action"
    },
    ngContentSelectors: _c0,
    decls: 3,
    vars: 2,
    consts: [[3, "ngTemplateOutlet"], [1, "k-spacer"], ["type", "button", "kendoButton", "", 3, "disabled", "fillMode", "themeColor", "ngClass", "svgIcon", "icon"], ["type", "button", "kendoButton", "", 3, "click", "disabled", "fillMode", "themeColor", "ngClass", "svgIcon", "icon"]],
    template: function DialogActionsComponent_Template(rf, ctx) {
      if (rf & 1) {
        i0.ɵɵprojectionDef();
        i0.ɵɵconditionalCreate(0, DialogActionsComponent_Conditional_0_Template, 1, 0);
        i0.ɵɵconditionalCreate(1, DialogActionsComponent_Conditional_1_Template, 2, 0)(2, DialogActionsComponent_Conditional_2_Template, 1, 1, "ng-container", 0);
      }
      if (rf & 2) {
        i0.ɵɵconditional(!ctx.actions ? 0 : -1);
        i0.ɵɵadvance();
        i0.ɵɵconditional(ctx.actionsArray ? 1 : 2);
      }
    },
    dependencies: [NgClass, NgTemplateOutlet, i1.ButtonComponent],
    encapsulation: 2
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DialogActionsComponent, [{
    type: Component,
    args: [{
      selector: 'kendo-dialog-actions',
      template: `
        @if (!actions) {
          <ng-content></ng-content>
        }
        @if (actionsArray) {
          @for (action of actionsArray; track action) {
            @if (isDivider(action)) {
              <span class="k-spacer"></span>
            } @else {
              <button
                type="button"
                kendoButton
                [disabled]="action.disabled"
                [fillMode]="action.fillMode"
                [themeColor]="action.themeColor"
                [ngClass]="action.cssClass"
                (click)="onButtonClick(action, $event)"
                [attr.aria-label]="action.text"
                [svgIcon]="action.svgIcon"
                [icon]="action.icon"
                >
                {{ action.text }}
              </button>
            }
          }
        } @else {
          <ng-container [ngTemplateOutlet]="actionsTemplate"></ng-container>
        }
        `,
      standalone: true,
      imports: [NgClass, NgTemplateOutlet, KENDO_BUTTON]
    }]
  }], () => [{
    type: i0.ElementRef
  }], {
    actions: [{
      type: Input
    }],
    layout: [{
      type: Input
    }],
    action: [{
      type: Output
    }],
    hostClasses: [{
      type: HostBinding,
      args: ['class.k-actions']
    }, {
      type: HostBinding,
      args: ['class.k-actions-horizontal']
    }, {
      type: HostBinding,
      args: ['class.k-window-actions']
    }, {
      type: HostBinding,
      args: ['class.k-dialog-actions']
    }],
    startClassName: [{
      type: HostBinding,
      args: ['class.k-actions-start']
    }],
    centerClassName: [{
      type: HostBinding,
      args: ['class.k-actions-center']
    }],
    endClassName: [{
      type: HostBinding,
      args: ['class.k-actions-end']
    }],
    stretchedClassName: [{
      type: HostBinding,
      args: ['class.k-actions-stretched']
    }]
  });
})();

/**
 * Represents a preventable event in Dialog and Window components.
 *
 */
class PreventableEvent {
  prevented = false;
  /**
   * @hidden
   */
  constructor() {}
  /**
   * Prevents the default action for a specified event.
   * In this way, the source component suppresses the built-in behavior that follows the event.
   */
  preventDefault() {
    this.prevented = true;
  }
  /**
   * If the event is prevented by any of its subscribers, returns `true`.
   *
   * @returns `true` if the default action was prevented. Otherwise, returns `false`.
   */
  isDefaultPrevented() {
    return this.prevented;
  }
}

/**
 * @hidden
 */
const DIALOG_LOCALIZATION_SERVICE = new InjectionToken('Dialog LocalizationService');

/**
 * @hidden
 */
class TitleBarLocalizationService extends LocalizationService {
  dialogLocalization;
  constructor(prefix, messageService, rtl, dialogLocalization) {
    super(prefix, messageService, rtl);
    this.dialogLocalization = dialogLocalization;
  }
  get(shortKey) {
    if (this.dialogLocalization) {
      return this.dialogLocalization.get(shortKey);
    }
    return super.get(shortKey);
  }
  static ɵfac = function TitleBarLocalizationService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || TitleBarLocalizationService)(i0.ɵɵinject(L10N_PREFIX), i0.ɵɵinject(i1$1.MessageService, 8), i0.ɵɵinject(RTL, 8), i0.ɵɵinject(DIALOG_LOCALIZATION_SERVICE, 8));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: TitleBarLocalizationService,
    factory: TitleBarLocalizationService.ɵfac
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(TitleBarLocalizationService, [{
    type: Injectable
  }], () => [{
    type: undefined,
    decorators: [{
      type: Inject,
      args: [L10N_PREFIX]
    }]
  }, {
    type: i1$1.MessageService,
    decorators: [{
      type: Optional
    }]
  }, {
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [RTL]
    }]
  }, {
    type: i1$1.LocalizationService,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [DIALOG_LOCALIZATION_SERVICE]
    }]
  }], null);
})();

/**
 * @hidden
 */
class Messages extends ComponentMessages {
  /**
   * Sets the title for the **Close** button.
   */
  closeTitle;
  /**
   * Sets the title for the **Restore** button.
   */
  restoreTitle;
  /**
   * Sets the title for the **Maximize** button.
   */
  maximizeTitle;
  /**
   * Sets the title for the **Minimize** button.
   */
  minimizeTitle;
  static ɵfac = /* @__PURE__ */(() => {
    let ɵMessages_BaseFactory;
    return function Messages_Factory(__ngFactoryType__) {
      return (ɵMessages_BaseFactory || (ɵMessages_BaseFactory = i0.ɵɵgetInheritedFactory(Messages)))(__ngFactoryType__ || Messages);
    };
  })();
  static ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
    type: Messages,
    selectors: [["kendo-dialog-messages-base"]],
    inputs: {
      closeTitle: "closeTitle",
      restoreTitle: "restoreTitle",
      maximizeTitle: "maximizeTitle",
      minimizeTitle: "minimizeTitle"
    },
    features: [i0.ɵɵInheritDefinitionFeature]
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(Messages, [{
    type: Directive,
    args: [{
      selector: 'kendo-dialog-messages-base'
    }]
  }], null, {
    closeTitle: [{
      type: Input
    }],
    restoreTitle: [{
      type: Input
    }],
    maximizeTitle: [{
      type: Input
    }],
    minimizeTitle: [{
      type: Input
    }]
  });
})();

/**
 * @hidden
 */
class LocalizedMessagesDirective extends Messages {
  service;
  constructor(service) {
    super();
    this.service = service;
  }
  static ɵfac = function LocalizedMessagesDirective_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || LocalizedMessagesDirective)(i0.ɵɵdirectiveInject(i1$1.LocalizationService));
  };
  static ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
    type: LocalizedMessagesDirective,
    selectors: [["", "kendoDialogLocalizedMessages", ""], ["", "kendoWindowLocalizedMessages", ""], ["", "kendoDialogTitleBarLocalizedMessages", ""]],
    features: [i0.ɵɵProvidersFeature([{
      provide: Messages,
      useExisting: forwardRef(() => LocalizedMessagesDirective)
    }]), i0.ɵɵInheritDefinitionFeature]
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(LocalizedMessagesDirective, [{
    type: Directive,
    args: [{
      providers: [{
        provide: Messages,
        useExisting: forwardRef(() => LocalizedMessagesDirective)
      }],
      selector: `
    [kendoDialogLocalizedMessages],
    [kendoWindowLocalizedMessages],
    [kendoDialogTitleBarLocalizedMessages]
  `,
      standalone: true
    }]
  }], () => [{
    type: i1$1.LocalizationService
  }], null);
})();

/**
 * Represents the [Kendo UI DialogTitleBar component for Angular](https://www.telerik.com/kendo-angular-ui/components/dialogs/api/dialogtitlebarcomponent).
 *
 * Use this component as part of the Dialog content when you create the Dialog dynamically with an [Angular service](https://www.telerik.com/kendo-angular-ui/components/dialogs/dialog/service).
 *
 * ```html
 * <kendo-dialog>
 *   <kendo-dialog-titlebar [id]="dialogId" (close)="onClose()">
 *      Dialog Title
 *   </kendo-dialog-titlebar>
 * </kendo-dialog>
 * ```
 *
 */
class DialogTitleBarComponent {
  zone;
  hostElement;
  localizationService;
  /**
   * Fires when the close button in the title bar is clicked.
   */
  close = new EventEmitter();
  /**
   * @hidden
   */
  id;
  /**
   * @hidden
   */
  closeTitle;
  /**
   * @hidden
   */
  closable = true;
  get className() {
    return true;
  }
  /**
   * @hidden
   */
  xIcon = xIcon;
  constructor(zone, hostElement, localizationService) {
    this.zone = zone;
    this.hostElement = hostElement;
    this.localizationService = localizationService;
  }
  get closeButtonTitle() {
    return this.closeTitle || this.localizationService.get('closeTitle');
  }
  ngAfterViewInit() {
    if (!this.id) {
      return;
    }
    this.zone.onStable.pipe(take(1)).subscribe(() => {
      const element = this.hostElement.nativeElement.querySelector('.k-dialog-title');
      element?.setAttribute('id', this.id);
    });
  }
  /**
   * @hidden
   */
  onCloseClick(e) {
    e.preventDefault();
    const eventArgs = new PreventableEvent();
    this.close.emit(eventArgs);
  }
  static ɵfac = function DialogTitleBarComponent_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DialogTitleBarComponent)(i0.ɵɵdirectiveInject(i0.NgZone), i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i1$1.LocalizationService, 8));
  };
  static ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
    type: DialogTitleBarComponent,
    selectors: [["kendo-dialog-titlebar"]],
    hostVars: 4,
    hostBindings: function DialogTitleBarComponent_HostBindings(rf, ctx) {
      if (rf & 2) {
        i0.ɵɵclassProp("k-window-titlebar", ctx.className)("k-dialog-titlebar", ctx.className);
      }
    },
    inputs: {
      id: "id",
      closeTitle: "closeTitle",
      closable: "closable"
    },
    outputs: {
      close: "close"
    },
    features: [i0.ɵɵProvidersFeature([TitleBarLocalizationService, {
      provide: LocalizationService,
      useExisting: TitleBarLocalizationService
    }, {
      provide: L10N_PREFIX,
      useValue: 'kendo.dialog'
    }])],
    ngContentSelectors: _c0,
    decls: 4,
    vars: 1,
    consts: () => {
      let i18n_0;
      if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
        /**
         * @desc The title of the close button
         * @meaning kendo.dialog.closeTitle
         */
        const MSG_D__PROJECTS_FOREVER_MEMORIES_PROJ_FOREVER_MEMORIES_FOREVER_WEB_NODE_MODULES__PROGRESS_KENDO_ANGULAR_DIALOG_FESM2022_PROGRESS_KENDO_ANGULAR_DIALOG_MJS_0 = goog.getMsg("Close");
        i18n_0 = MSG_D__PROJECTS_FOREVER_MEMORIES_PROJ_FOREVER_MEMORIES_FOREVER_WEB_NODE_MODULES__PROGRESS_KENDO_ANGULAR_DIALOG_FESM2022_PROGRESS_KENDO_ANGULAR_DIALOG_MJS_0;
      } else {
        i18n_0 = $localize`:kendo.dialog.closeTitle|The title of the close button:Close`;
      }
      return [["kendoDialogTitleBarLocalizedMessages", "", "closeTitle", i18n_0], [1, "k-window-title", "k-dialog-title"], [1, "k-window-titlebar-actions", "k-dialog-titlebar-actions"], ["kendoButton", "", "fillMode", "flat", "type", "button", "icon", "close", "size", "xsmall", 1, "k-window-titlebar-action", "k-dialog-titlebar-action", 3, "click", "svgIcon"]];
    },
    template: function DialogTitleBarComponent_Template(rf, ctx) {
      if (rf & 1) {
        i0.ɵɵprojectionDef();
        i0.ɵɵelementContainerStart(0, 0);
        i0.ɵɵelementStart(1, "span", 1);
        i0.ɵɵprojection(2);
        i0.ɵɵelementEnd();
        i0.ɵɵconditionalCreate(3, DialogTitleBarComponent_Conditional_3_Template, 2, 3, "div", 2);
        i0.ɵɵelementContainerEnd();
      }
      if (rf & 2) {
        i0.ɵɵadvance(3);
        i0.ɵɵconditional(ctx.closable ? 3 : -1);
      }
    },
    dependencies: [LocalizedMessagesDirective, ButtonComponent],
    encapsulation: 2
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DialogTitleBarComponent, [{
    type: Component,
    args: [{
      selector: 'kendo-dialog-titlebar',
      providers: [TitleBarLocalizationService, {
        provide: LocalizationService,
        useExisting: TitleBarLocalizationService
      }, {
        provide: L10N_PREFIX,
        useValue: 'kendo.dialog'
      }],
      template: `
         <ng-container
            kendoDialogTitleBarLocalizedMessages
            i18n-closeTitle="kendo.dialog.closeTitle|The title of the close button"
            closeTitle="Close"
        >
            <span class="k-window-title k-dialog-title">
                <ng-content></ng-content>
            </span>

            @if (closable) {
                <div class="k-window-titlebar-actions k-dialog-titlebar-actions">
                    <button
                        kendoButton
                        fillMode="flat"
                        type="button"
                        [attr.title]="closeButtonTitle"
                        [attr.aria-label]="closeButtonTitle"
                        icon="close"
                        size="xsmall"
                        [svgIcon]="xIcon"
                        class="k-window-titlebar-action k-dialog-titlebar-action"
                        (click)="onCloseClick($event)"
                    >
                    </button>
                </div>
            }
        </ng-container>
    `,
      standalone: true,
      imports: [LocalizedMessagesDirective, ButtonComponent]
    }]
  }], () => [{
    type: i0.NgZone
  }, {
    type: i0.ElementRef
  }, {
    type: i1$1.LocalizationService,
    decorators: [{
      type: Optional
    }]
  }], {
    close: [{
      type: Output
    }],
    id: [{
      type: Input
    }],
    closeTitle: [{
      type: Input
    }],
    closable: [{
      type: Input
    }],
    className: [{
      type: HostBinding,
      args: ['class.k-window-titlebar']
    }, {
      type: HostBinding,
      args: ['class.k-dialog-titlebar']
    }]
  });
})();

/**
 * @hidden
 */
const packageMetadata = {
  name: '@progress/kendo-angular-dialog',
  productName: 'Kendo UI for Angular',
  productCode: 'KENDOUIANGULAR',
  productCodes: ['KENDOUIANGULAR'],
  publishDate: 1780593727,
  version: '24.1.0',
  licensingDocsUrl: 'https://www.telerik.com/kendo-angular-ui/my-license/'
};

/**
 * @hidden
 */
const isPresent = value => value !== null && value !== undefined;
/**
 * @hidden
 */
const isTruthy = value => !!value;
const toClassList = classNames => String(classNames).trim().split(' ');
const focusableRegex = /^(?:a|input|select|textarea|button|object)$/i;
/**
 * @hidden
 */
const DIALOG_ELEMENTS_HANDLING_ESC_KEY = 'k-dialog-wrapper k-actions k-dialog-titlebar-action';
/**
 * @hidden
 */
const DIALOG_ELEMENTS_HANDLING_ARROWS = 'k-actions';
/**
 * @hidden
 */
const WINDOW_CLASSES = 'k-window';
/**
 * @hidden
 */
const WINDOW_ELEMENTS_HANDLING_ESC_KEY = 'k-window k-window-titlebar-action';
/**
 * @hidden
 */
const hasClasses = (element, classNames) => {
  const namesList = toClassList(classNames);
  return Boolean(toClassList(element.className).find(className => namesList.indexOf(className) >= 0));
};
/**
 * @hidden
 */
const isVisible = element => {
  if (!element?.getBoundingClientRect) {
    return false;
  }
  const rect = element.getBoundingClientRect();
  return !!(rect.width && rect.height) && window.getComputedStyle(element).visibility !== 'hidden';
};
/**
 * @hidden
 */
const isFocusable = (element, checkVisibility = true) => {
  if (element.tagName) {
    const tagName = element.tagName.toLowerCase();
    const tabIndex = element.getAttribute('tabIndex');
    const validTabIndex = tabIndex !== null && !isNaN(tabIndex) && tabIndex > -1;
    let focusable = false;
    if (focusableRegex.test(tagName)) {
      focusable = !element.disabled;
    } else {
      focusable = validTabIndex;
    }
    return focusable && (!checkVisibility || isVisible(element));
  }
  return false;
};
/**
 * Receives CSS class declarations either as an object, string or array and returns an array of the class names.
 *
 * @hidden
 */
const parseCSSClassNames = value => {
  if (isObject(value)) {
    return parseObjectClassNames(value);
  }
  if (isString(value)) {
    return parseStringClassNames(value);
  }
  if (Array.isArray(value)) {
    return parseArrayClassNames(value);
  }
};
const parseObjectClassNames = value => {
  const classes = [];
  Object.keys(value).forEach(className => {
    const currentClassName = splitStringToArray(className);
    if (value[className] && currentClassName[0]) {
      classes.push(...currentClassName);
    }
  });
  return classes;
};
const parseStringClassNames = value => {
  const classes = [];
  const classesArray = splitStringToArray(value);
  classesArray.forEach(className => {
    classes.push(className);
  });
  return classes;
};
const parseArrayClassNames = value => {
  const classes = [];
  value.forEach(className => {
    const current = splitStringToArray(className);
    if (current[0]) {
      classes.push(...current);
    }
  });
  return classes;
};
/**
 * @hidden
 */
const preventDefault = ({
  originalEvent: event
}) => {
  event.stopPropagation();
  event.preventDefault();
};
/**
 * @hidden
 */
const isWindowAvailable = () => {
  return typeof window !== 'undefined';
};
/**
 * @hidden
 */
const preventOnDblClick = release => mouseDown => of(mouseDown).pipe(delay(150), takeUntil(release));
/**
 * @hidden
 */
const RESIZE_DIRECTIONS = ['n', 'e', 's', 'w', 'se', 'sw', 'ne', 'nw'];
/**
 * @hidden
 */
const OFFSET_STYLES = ['top', 'left', 'width', 'height'];
/**
 * @hidden
 */
const isString = value => value instanceof String || typeof value === 'string';
/**
 * @hidden
 */
const isObject = value => isPresent(value) && !Array.isArray(value) && typeof value === 'object';
/**
 * @hidden
 */
const isNumber = value => typeof value === 'number' && isFinite(value);
/**
 * @hidden
 */
const createValueWithUnit = value => value + (isNumber(value) ? 'px' : '');
/**
 * @hidden
 */
const splitStringToArray = value => value.trim().replace(/\s+/g, " ").split(' ');
/**
 * @hidden
 */
const findPrimaryButton = buttons => {
  for (let i = buttons.length - 1; i >= 0; i--) {
    const classList = buttons[i].classList;
    for (let j = 0; j < classList.length; j++) {
      if (classList[j].endsWith('-primary')) {
        return buttons[i];
      }
    }
  }
};

/**
 * Represents the result when the **Close** button is clicked in a Dialog opened through `DialogService`.
 *
 * Used when the results from the Dialogs that are opened through `DialogService` are filtered. ([See example.](https://www.telerik.com/kendo-angular-ui/components/dialogs/api/dialogservice#open))
 *
 */
class DialogCloseResult {}

/**
 * @hidden
 */
function animations(duration, direction, animationType) {
  switch (animationType) {
    case 'slide':
      {
        const translate = direction === 'left' || direction === 'right' ? 'translateX' : 'translateY';
        const start = direction === 'right' || direction === 'down' ? -100 : 100;
        const end = 0;
        return [style({
          transform: `${translate}(${start}%)`
        }), animate(`${duration}ms ease-in`, style({
          transform: `${translate}(${end}%)`
        }))];
      }
    case 'expand':
      {
        const scale = direction === 'up' || direction === 'down' ? 'scaleY' : 'scaleX';
        const startScale = 0;
        const endScale = 1;
        let origin;
        if (direction === 'down') {
          origin = 'top';
        } else if (direction === 'left') {
          origin = 'right';
        } else if (direction === 'right') {
          origin = 'left';
        } else {
          origin = 'bottom';
        }
        return [style({
          transform: `${scale}(${startScale})`,
          transformOrigin: origin
        }), animate(`${duration}ms ease-in`, style({
          transform: `${scale}(${endScale})`
        }))];
      }
    case 'zoom':
      {
        const startZoom = 0;
        const endZoom = 1;
        return [animate(duration, keyframes([style({
          transform: `scale(${startZoom})`
        }), style({
          transform: `scale(${endZoom})`
        })]))];
      }
    case 'fade':
      {
        const startFade = 0;
        const endFade = 1;
        return [animate(duration, keyframes([style({
          opacity: `${startFade}`
        }), style({
          opacity: `${endFade}`
        })]))];
      }
    // 'translate' is considered the default animation type
    default:
      return [style({
        transform: 'translate(0, -10%)'
      }), animate(`${duration}ms cubic-bezier(.2, 1, .2, 1)`)];
  }
}

/**
 * @hidden
 */
const createPlayer = (builder, animation, animatedElement) => {
  const factory = builder.build(animation);
  let player = factory.create(animatedElement);
  player.onDone(() => {
    if (player) {
      player.destroy();
      player = null;
    }
  });
  return player;
};

/**
 * @hidden
 */
const animateContent = (animation, defAnimationConfig, animatedElement, builder) => {
  let animationConfig = defAnimationConfig;
  if (typeof animation !== 'boolean') {
    animationConfig = animation;
    animationConfig.duration = animationConfig.duration ? animationConfig.duration : defAnimationConfig.duration;
  }
  const animationSpecs = animations(animationConfig.duration, animationConfig.direction, animationConfig.type);
  const player = createPlayer(builder, animationSpecs, animatedElement);
  player.play();
};
const DEFAULT_ANIMATION_CONFIG = {
  duration: 300,
  type: 'translate'
};
/**
 * Represents the [Kendo UI Dialog component for Angular](https://www.telerik.com/kendo-angular-ui/components/dialogs/dialog).
 *
 * Use this component to display modal dialog windows in your application.
 *
 * @example
 * ```ts
 * import { Component } from '@angular/core';
 *
 * @Component({
 *   selector: 'my-app',
 *   template: `
 *     <kendo-dialog title="Example Dialog">
 *       <p>Dialog content goes here.</p>
 *     </kendo-dialog>
 *   `
 * })
 * export class AppComponent {}
 * ```
 *
 * @remarks
 * Supported children components are: {@link DialogTitleBarComponent}, {@link DialogActionsComponent}, {@link CustomMessagesComponent}.
 */
class DialogComponent {
  wrapper;
  renderer;
  cdr;
  ngZone;
  builder;
  /**
   * Specifies the action buttons to render in the Dialog.
   *
   * @type {DialogAction[]}
   */
  actions;
  /**
   * Sets the layout of the action buttons in the Dialog. Applies only if you specify action buttons through the `actions` option.
   *
   * @type {ActionsLayout}
   * @default 'stretched'
   */
  actionsLayout = 'stretched';
  /**
   * Sets the query selector for the element to receive initial focus. ([See examples.](https://www.telerik.com/kendo-angular-ui/components/dialogs/dialog/initial-focus))
   *
   * @type {string}
   *
   * @remarks
   * This property is related to accessibility.
   */
  autoFocusedElement;
  /**
   * Specifies whether the Dialog can be closed by the **Close** button in the title bar or by pressing the `Esc` key.
   *
   * @type {boolean}
   * @default true
   */
  set closable(value) {
    this._closable = value;
    if (this.titlebarContent?.first) {
      this.titlebarContent.first.closable = value;
    }
  }
  get closable() {
    return this._closable;
  }
  /**
   * Sets the text in the Dialog title bar.
   *
   * @type {string}
   */
  title;
  /**
   * Sets the width of the Dialog. Use a number for pixels or a string for units (for example, `50%`).
   *
   * @type {number | string}
   */
  width;
  /**
   * Sets the minimum width of the Dialog. Use a number for pixels or a string for units (for example, `50%`).
   *
   * @type {number | string}
   */
  minWidth;
  /**
   * Sets the maximum width of the Dialog. Use a number for pixels or a string for units (for example, `50%`).
   *
   * @type {number | string}
   */
  maxWidth;
  /**
   * Sets the height of the Dialog. Use a number for pixels or a string for units (for example, `50%`).
   *
   * @type {number | string}
   */
  height;
  /**
   * Sets the minimum height of the Dialog. Use a number for pixels or a string for units (for example, `50%`).
   *
   * @type {number | string}
   */
  minHeight;
  /**
   * Sets the maximum height of the Dialog. Use a number for pixels or a string for units (for example, `50%`).
   *
   * @type {number | string}
   */
  maxHeight;
  /**
   * Configures the Dialog opening animation ([see example](https://www.telerik.com/kendo-angular-ui/components/dialogs/dialog/animations)).
   * The default animation type is `translate` and the duration is `300ms`.
   *
   * @type {boolean | DialogAnimation}
   * @default true
   */
  animation = true;
  /**
   * @hidden
   */
  set htmlAttributes(attributes) {
    setHTMLAttributes(attributes, this.renderer, this.wrapper.nativeElement);
    const el = this.wrapper.nativeElement;
    const dir = el.getAttribute('dir');
    const tIndex = el.getAttribute('tabindex');
    if (this.direction !== dir && dir) {
      this.direction = dir;
    }
    if (this.tabIndex !== tIndex && tIndex) {
      this.tabIndex = tIndex;
    }
    this._htmlAttributes = attributes;
  }
  get htmlAttributes() {
    return this._htmlAttributes;
  }
  /**
   * @hidden
   */
  set cssClass(classes) {
    this.setServiceClasses(this._cssClass, classes);
    this._cssClass = classes;
  }
  get cssClass() {
    return this._cssClass;
  }
  /**
   * @hidden
   */
  contentTemplate;
  /**
   * @hidden
   */
  titleId = null;
  /**
   * @hidden
   */
  contentId = null;
  /**
   * @hidden
   */
  closeTitle;
  /**
   * @hidden
   */
  showLicenseWatermark = false;
  /**
   * @hidden
   */
  licenseMessage;
  /**
   * Emits when the user clicks an action button in the Dialog. Fires only if you specify action buttons through the `actions` option.
   *
   * @type {EventEmitter<DialogAction>}
   */
  action = new EventEmitter();
  /**
   * Emits when the user clicks the **Close** button or presses the `ESC` key.
   *
   * @type {EventEmitter<any>}
   */
  close = new EventEmitter();
  get dir() {
    return this.direction;
  }
  tabIndex = 0;
  titlebarContent;
  titlebarView;
  actionsView;
  dialog;
  _htmlAttributes;
  _cssClass;
  _closable = true;
  direction;
  subscriptions = [];
  domSubs = new Subscription();
  constructor(wrapper, renderer, localization, cdr, ngZone, builder) {
    this.wrapper = wrapper;
    this.renderer = renderer;
    this.cdr = cdr;
    this.ngZone = ngZone;
    this.builder = builder;
    const isValid = validatePackage(packageMetadata);
    this.licenseMessage = getLicenseMessage(packageMetadata);
    this.showLicenseWatermark = shouldShowValidationUI(isValid);
    this.direction = localization.rtl ? 'rtl' : 'ltr';
    this.subscriptions.push(localization.changes.subscribe(({
      rtl
    }) => this.direction = rtl ? 'rtl' : 'ltr'));
    this.titleId = this.generateTitleId();
    this.contentId = this.generateContentId();
  }
  ngAfterContentInit() {
    this.bubble('close', this.titlebarContent.first);
    this.renderer.setAttribute(this.wrapper.nativeElement.querySelector('.k-dialog'), 'aria-describedby', this.contentId);
    if (this.titlebarContent.first) {
      this.titlebarContent.first.closable = this.closable;
    } else {
      this.subscriptions.push(this.titlebarContent.changes.subscribe(() => {
        if (isPresent(this.titlebarContent.first)) {
          this.titlebarContent.first.closable = this.closable;
          this.ngZone.onStable.pipe(take(1)).subscribe(() => {
            this.bubble('close', this.titlebarContent.first);
          });
        }
      }));
    }
  }
  ngAfterViewInit() {
    if (!isDocumentAvailable()) {
      return;
    }
    this.ngZone.onStable.pipe(take(1)).subscribe(() => {
      this.handleInitialFocus();
    });
    this.bubble('close', this.titlebarView.first);
    this.bubble('action', this.actionsView);
    if (this.titlebarView.first) {
      this.renderer.setAttribute(this.wrapper.nativeElement.querySelector('.k-dialog'), 'aria-labelledby', this.titleId);
    } else {
      this.subscriptions.push(this.titlebarView.changes.subscribe(() => {
        if (isPresent(this.titlebarView.first)) {
          this.titlebarView.first.id = this.titleId;
          this.ngZone.onStable.pipe(take(1)).subscribe(() => {
            this.bubble('close', this.titlebarView.first);
            this.renderer.setAttribute(this.wrapper.nativeElement.querySelector('.k-dialog'), 'aria-labelledby', this.titleId);
          });
        }
      }));
    }
    this.initDomEvents();
  }
  ngOnInit() {
    if (this.animation) {
      animateContent(this.animation, DEFAULT_ANIMATION_CONFIG, this.dialog.nativeElement, this.builder);
    }
    this.renderer.removeAttribute(this.wrapper.nativeElement, 'title');
    this.cdr.detectChanges();
  }
  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
    this.subscriptions = [];
    if (this.domSubs) {
      this.domSubs.unsubscribe();
    }
  }
  /**
   * Focuses the wrapper of the Dialog component.
   */
  focus() {
    const wrapper = this.wrapper.nativeElement;
    if (isPresent(wrapper)) {
      wrapper.focus();
    }
  }
  initDomEvents() {
    if (!this.wrapper) {
      return;
    }
    this.ngZone.runOutsideAngular(() => {
      this.domSubs.add(this.renderer.listen(this.wrapper.nativeElement, 'keydown', ev => {
        this.onKeyDown(ev);
      }));
    });
  }
  onKeyDown(event) {
    const target = event.target;
    const parent = target.parentElement;
    const code = normalizeKeys(event);
    if (hasClasses(target, DIALOG_ELEMENTS_HANDLING_ESC_KEY) || hasClasses(parent, DIALOG_ELEMENTS_HANDLING_ESC_KEY)) {
      if (code === Keys.Escape && this.closable) {
        this.ngZone.run(() => {
          this.close.emit(new DialogCloseResult());
        });
      }
    }
    if (hasClasses(target, 'k-button') && hasClasses(parent, DIALOG_ELEMENTS_HANDLING_ARROWS) && (code === Keys.ArrowLeft || code === Keys.ArrowRight)) {
      this.ngZone.run(() => {
        this.handleActionButtonFocus(parent, code);
      });
    }
    if (code === Keys.Tab) {
      this.ngZone.run(() => {
        this.keepFocusWithinComponent(target, event);
      });
    }
  }
  setServiceClasses(prevValue, value) {
    const el = this.wrapper.nativeElement;
    if (prevValue) {
      parseCSSClassNames(prevValue).forEach(className => {
        this.renderer.removeClass(el, className);
      });
    }
    if (value) {
      parseCSSClassNames(value).forEach(className => {
        this.renderer.addClass(el, className);
      });
    }
  }
  /**
   * @hidden
   */
  handleInitialFocus() {
    const wrapper = this.wrapper.nativeElement;
    const primaryButton = this.findPrimary(wrapper);
    if (this.autoFocusedElement) {
      const initiallyFocusedElement = wrapper.querySelector(this.autoFocusedElement);
      if (initiallyFocusedElement) {
        initiallyFocusedElement.focus();
      }
    } else if (this.shouldFocusPrimary(primaryButton)) {
      primaryButton.focus();
    } else {
      wrapper.focus();
    }
  }
  /**
   * @hidden
   */
  findPrimary(wrapper) {
    const actionBtns = wrapper.querySelectorAll('.k-actions .k-button');
    return findPrimaryButton(actionBtns);
  }
  /**
   * @hidden
   */
  handleActionButtonFocus(parent, key) {
    const focusableActionButtons = this.getAllFocusableChildren(parent);
    for (let i = 0; i < focusableActionButtons.length; i++) {
      const current = focusableActionButtons[i];
      if (current === document.activeElement) {
        if (key === Keys.ArrowLeft && i > 0) {
          focusableActionButtons[i - 1].focus();
          break;
        }
        if (key === Keys.ArrowRight && i < focusableActionButtons.length - 1) {
          focusableActionButtons[i + 1].focus();
          break;
        }
      }
    }
  }
  /**
   * @hidden
   */
  keepFocusWithinComponent(target, event) {
    const wrapper = this.wrapper.nativeElement;
    const [firstFocusable, lastFocusable] = this.getFirstAndLastFocusable(wrapper);
    const tabAfterLastFocusable = !event.shiftKey && target === lastFocusable;
    const shiftTabAfterFirstFocusable = event.shiftKey && target === firstFocusable;
    if (tabAfterLastFocusable) {
      event.preventDefault();
      firstFocusable.focus();
    }
    if (shiftTabAfterFirstFocusable) {
      event.preventDefault();
      lastFocusable.focus();
    }
  }
  /**
   * @hidden
   */
  shouldFocusPrimary(el) {
    return isPresent(el) && isFocusable(el);
  }
  /**
   * @hidden
   */
  getAllFocusableChildren(parent) {
    return parent.querySelectorAll(focusableSelector);
  }
  /**
   * @hidden
   */
  getFirstAndLastFocusable(parent) {
    const all = this.getAllFocusableChildren(parent);
    const firstFocusable = all.length > 0 ? all[0] : parent;
    const lastFocusable = all.length > 0 ? all[all.length - 1] : parent;
    return [firstFocusable, lastFocusable];
  }
  /**
   * @hidden
   */
  generateTitleId() {
    return 'kendo-dialog-title-' + Math.ceil(Math.random() * 1000000).toString();
  }
  /**
   * @hidden
   */
  generateContentId() {
    return 'kendo-dialog-content-' + Math.ceil(Math.random() * 1000000).toString();
  }
  get wrapperClass() {
    return true;
  }
  get styles() {
    const styles = {};
    if (this.width) {
      styles.width = createValueWithUnit(this.width);
    }
    if (this.height) {
      styles.height = createValueWithUnit(this.height);
    }
    if (this.minWidth) {
      styles.minWidth = createValueWithUnit(this.minWidth);
    }
    if (this.maxWidth) {
      styles.maxWidth = createValueWithUnit(this.maxWidth);
    }
    if (this.minHeight) {
      styles.minHeight = createValueWithUnit(this.minHeight);
    }
    if (this.maxHeight) {
      styles.maxHeight = createValueWithUnit(this.maxHeight);
    }
    return styles;
  }
  bubble(eventName, component) {
    if (component) {
      const emit = e => this[eventName].emit(e);
      const s = component[eventName].subscribe(emit);
      this.subscriptions.push(s);
    }
  }
  static ɵfac = function DialogComponent_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DialogComponent)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.Renderer2), i0.ɵɵdirectiveInject(i1$1.LocalizationService), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(i0.NgZone), i0.ɵɵdirectiveInject(i2.AnimationBuilder));
  };
  static ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
    type: DialogComponent,
    selectors: [["kendo-dialog"]],
    contentQueries: function DialogComponent_ContentQueries(rf, ctx, dirIndex) {
      if (rf & 1) {
        i0.ɵɵcontentQuery(dirIndex, DialogTitleBarComponent, 4);
      }
      if (rf & 2) {
        let _t;
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.titlebarContent = _t);
      }
    },
    viewQuery: function DialogComponent_Query(rf, ctx) {
      if (rf & 1) {
        i0.ɵɵviewQuery(DialogActionsComponent, 5)(_c1, 7)(DialogTitleBarComponent, 5);
      }
      if (rf & 2) {
        let _t;
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.actionsView = _t.first);
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.dialog = _t.first);
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.titlebarView = _t);
      }
    },
    hostVars: 4,
    hostBindings: function DialogComponent_HostBindings(rf, ctx) {
      if (rf & 2) {
        i0.ɵɵattribute("dir", ctx.dir)("tabIndex", ctx.tabIndex);
        i0.ɵɵclassProp("k-dialog-wrapper", ctx.wrapperClass);
      }
    },
    inputs: {
      actions: "actions",
      actionsLayout: "actionsLayout",
      autoFocusedElement: "autoFocusedElement",
      closable: "closable",
      title: "title",
      width: "width",
      minWidth: "minWidth",
      maxWidth: "maxWidth",
      height: "height",
      minHeight: "minHeight",
      maxHeight: "maxHeight",
      animation: "animation"
    },
    outputs: {
      action: "action",
      close: "close"
    },
    exportAs: ["kendoDialog"],
    features: [i0.ɵɵProvidersFeature([LocalizationService, {
      provide: DIALOG_LOCALIZATION_SERVICE,
      useExisting: LocalizationService
    }, {
      provide: L10N_PREFIX,
      useValue: 'kendo.dialog'
    }, {
      provide: KENDO_WEBMCP_HOST,
      useExisting: forwardRef(() => DialogComponent)
    }])],
    ngContentSelectors: _c3,
    decls: 12,
    vars: 10,
    consts: () => {
      let i18n_1;
      if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
        /**
         * @desc The title of the close button
         * @meaning kendo.dialog.closeTitle
         */
        const MSG_D__PROJECTS_FOREVER_MEMORIES_PROJ_FOREVER_MEMORIES_FOREVER_WEB_NODE_MODULES__PROGRESS_KENDO_ANGULAR_DIALOG_FESM2022_PROGRESS_KENDO_ANGULAR_DIALOG_MJS_1 = goog.getMsg("Close");
        i18n_1 = MSG_D__PROJECTS_FOREVER_MEMORIES_PROJ_FOREVER_MEMORIES_FOREVER_WEB_NODE_MODULES__PROGRESS_KENDO_ANGULAR_DIALOG_FESM2022_PROGRESS_KENDO_ANGULAR_DIALOG_MJS_1;
      } else {
        i18n_1 = $localize`:kendo.dialog.closeTitle|The title of the close button:Close`;
      }
      return [["dialog", ""], ["kendoDialogLocalizedMessages", "", "closeTitle", i18n_1], [1, "k-overlay"], ["role", "dialog", "aria-modal", "true", 1, "k-window", "k-dialog", 3, "ngStyle"], [3, "closable", "closeTitle", "id"], [1, "k-window-content", "k-dialog-content", 3, "id"], [3, "ngTemplateOutlet"], [3, "actions", "layout"], ["kendoWatermarkOverlay", "", 3, "licenseMessage"]];
    },
    template: function DialogComponent_Template(rf, ctx) {
      if (rf & 1) {
        i0.ɵɵprojectionDef(_c2);
        i0.ɵɵelementContainerStart(0, 1);
        i0.ɵɵelement(1, "div", 2);
        i0.ɵɵelementStart(2, "div", 3, 0);
        i0.ɵɵconditionalCreate(4, DialogComponent_Conditional_4_Template, 2, 4, "kendo-dialog-titlebar", 4);
        i0.ɵɵconditionalCreate(5, DialogComponent_Conditional_5_Template, 1, 0);
        i0.ɵɵelementStart(6, "div", 5);
        i0.ɵɵconditionalCreate(7, DialogComponent_Conditional_7_Template, 1, 0);
        i0.ɵɵconditionalCreate(8, DialogComponent_Conditional_8_Template, 1, 1, null, 6);
        i0.ɵɵelementEnd();
        i0.ɵɵconditionalCreate(9, DialogComponent_Conditional_9_Template, 1, 0);
        i0.ɵɵconditionalCreate(10, DialogComponent_Conditional_10_Template, 1, 2, "kendo-dialog-actions", 7);
        i0.ɵɵconditionalCreate(11, DialogComponent_Conditional_11_Template, 1, 1, "div", 8);
        i0.ɵɵelementEnd();
        i0.ɵɵelementContainerEnd();
      }
      if (rf & 2) {
        i0.ɵɵadvance();
        i0.ɵɵproperty("@overlayAppear", undefined);
        i0.ɵɵadvance();
        i0.ɵɵproperty("ngStyle", ctx.styles);
        i0.ɵɵadvance(2);
        i0.ɵɵconditional(ctx.title ? 4 : -1);
        i0.ɵɵadvance();
        i0.ɵɵconditional(!ctx.title ? 5 : -1);
        i0.ɵɵadvance();
        i0.ɵɵproperty("id", ctx.contentId);
        i0.ɵɵadvance();
        i0.ɵɵconditional(!ctx.contentTemplate ? 7 : -1);
        i0.ɵɵadvance();
        i0.ɵɵconditional(ctx.contentTemplate ? 8 : -1);
        i0.ɵɵadvance();
        i0.ɵɵconditional(!ctx.actions ? 9 : -1);
        i0.ɵɵadvance();
        i0.ɵɵconditional(ctx.actions ? 10 : -1);
        i0.ɵɵadvance();
        i0.ɵɵconditional(ctx.showLicenseWatermark ? 11 : -1);
      }
    },
    dependencies: [LocalizedMessagesDirective, NgStyle, DialogTitleBarComponent, NgTemplateOutlet, DialogActionsComponent, WatermarkOverlayComponent],
    encapsulation: 2,
    data: {
      animation: [trigger('overlayAppear', [state('in', style({
        opacity: 1
      })), transition('void => *', [style({
        opacity: 0.1
      }), animate('.3s cubic-bezier(.2, .6, .4, 1)')])])]
    }
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DialogComponent, [{
    type: Component,
    args: [{
      animations: [trigger('overlayAppear', [state('in', style({
        opacity: 1
      })), transition('void => *', [style({
        opacity: 0.1
      }), animate('.3s cubic-bezier(.2, .6, .4, 1)')])])],
      exportAs: 'kendoDialog',
      providers: [LocalizationService, {
        provide: DIALOG_LOCALIZATION_SERVICE,
        useExisting: LocalizationService
      }, {
        provide: L10N_PREFIX,
        useValue: 'kendo.dialog'
      }, {
        provide: KENDO_WEBMCP_HOST,
        useExisting: forwardRef(() => DialogComponent)
      }],
      selector: 'kendo-dialog',
      template: `
        <ng-container
          kendoDialogLocalizedMessages
          i18n-closeTitle="kendo.dialog.closeTitle|The title of the close button"
          closeTitle="Close"
          >
          <div class="k-overlay" @overlayAppear></div>

          <div #dialog class="k-window k-dialog" role="dialog" aria-modal="true" [ngStyle]="styles">
            @if (title) {
              <kendo-dialog-titlebar [closable]="closable" [closeTitle]="closeTitle" [id]="titleId">{{ title }}</kendo-dialog-titlebar>
            }
            @if (!title) {
              <ng-content select="kendo-dialog-titlebar"></ng-content>
            }

            <div [id]="contentId" class="k-window-content k-dialog-content">
              @if (!contentTemplate) {
                <ng-content></ng-content>
              }
              @if (contentTemplate) {
                <ng-template [ngTemplateOutlet]="contentTemplate"></ng-template>
              }
            </div>

            @if (!actions) {
              <ng-content select="kendo-dialog-actions"></ng-content>
            }
            @if (actions) {
              <kendo-dialog-actions [actions]="actions" [layout]="actionsLayout"> </kendo-dialog-actions>
            }

            @if (showLicenseWatermark) {
              <div kendoWatermarkOverlay [licenseMessage]="licenseMessage"></div>
            }
          </div>
        </ng-container>
        `,
      standalone: true,
      imports: [LocalizedMessagesDirective, NgStyle, DialogTitleBarComponent, NgTemplateOutlet, DialogActionsComponent, WatermarkOverlayComponent]
    }]
  }], () => [{
    type: i0.ElementRef
  }, {
    type: i0.Renderer2
  }, {
    type: i1$1.LocalizationService
  }, {
    type: i0.ChangeDetectorRef
  }, {
    type: i0.NgZone
  }, {
    type: i2.AnimationBuilder
  }], {
    actions: [{
      type: Input
    }],
    actionsLayout: [{
      type: Input
    }],
    autoFocusedElement: [{
      type: Input
    }],
    closable: [{
      type: Input
    }],
    title: [{
      type: Input
    }],
    width: [{
      type: Input
    }],
    minWidth: [{
      type: Input
    }],
    maxWidth: [{
      type: Input
    }],
    height: [{
      type: Input
    }],
    minHeight: [{
      type: Input
    }],
    maxHeight: [{
      type: Input
    }],
    animation: [{
      type: Input
    }],
    action: [{
      type: Output
    }],
    close: [{
      type: Output
    }],
    dir: [{
      type: HostBinding,
      args: ['attr.dir']
    }],
    tabIndex: [{
      type: HostBinding,
      args: ['attr.tabIndex']
    }],
    titlebarContent: [{
      type: ContentChildren,
      args: [DialogTitleBarComponent, {
        descendants: false
      }]
    }],
    titlebarView: [{
      type: ViewChildren,
      args: [DialogTitleBarComponent]
    }],
    actionsView: [{
      type: ViewChild,
      args: [DialogActionsComponent, {
        static: false
      }]
    }],
    dialog: [{
      type: ViewChild,
      args: ['dialog', {
        static: true
      }]
    }],
    wrapperClass: [{
      type: HostBinding,
      args: ['class.k-dialog-wrapper']
    }]
  });
})();

/**
 * Holds references to the Dialog instance and published events when you open a Dialog through the `DialogService`.
 *
 * Use the `DialogRef` class to control and interact with Dialogs opened programmatically. ([See example.](https://www.telerik.com/kendo-angular-ui/components/dialogs/api/dialogservice#open))
 *
 */
class DialogRef {
  /**
   * Emits events when the Dialog is closed either through the **Close** button of the title bar or through the action buttons. If the **Close** button of the title bar is clicked, `DialogResult` is a `DialogCloseResult` instance. If the Dialog is closed through the action buttons, `DialogResult` contains the object that was passed when the Dialog was opened. When `close` is called with an argument, the result is the passed argument.
   */
  result;
  /**
   * A reference to the Dialog instance.
   */
  dialog;
  /**
   * A reference to the child component of the Dialog. Available when the Dialog is opened with [component content](https://www.telerik.com/kendo-angular-ui/components/dialogs/dialog/service#rendering-the-content-area).
   */
  content;
  /**
   * Closes the Dialog programmatically. When called without a value, the resulting Observable emits an empty `DialogCloseResult` object. When called with a value, the resulting Observable emits the provided value.
   *
   * ```ts
   * // Close without arguments: Returns an empty `DialogCloseResult` object.
   * dialogRef.close();
   *
   * // Simulate clicking an action button: Pass a `DialogAction` object.
   * dialogRef.close({ text: 'OK', primary: true });
   *
   * // Return custom data: Pass a custom object.
   * dialogRef.close({ success: true, data: { id: 123 } });
   * ```
   */
  close;
}

/**
 * Serves as the base class for a component provided as Dialog content through the `content` property.
 * ([See example.](https://www.telerik.com/kendo-angular-ui/components/dialogs/dialog/service#single-component-rendering)).
 *
 */
class DialogContentBase {
  dialog;
  /**
   * @hidden
   */
  dialogTitleBar;
  /**
   * @hidden
   */
  dialogActions;
  constructor(dialog) {
    this.dialog = dialog;
  }
  /**
   * @hidden
   */
  ngAfterViewInit() {
    if (this.dialogTitleBar) {
      // when opening component inside dialog with service AND the component has defined its own titlebar
      this.dialogTitleBar.close.pipe(filter(e => !e.isDefaultPrevented())).subscribe(() => {
        this.dialog.close();
      });
    }
    if (this.dialogActions) {
      if (this.dialogActions.actions) {
        this.dialogActions.action.subscribe(action => this.dialog.dialog.instance.action.emit(action));
      }
    }
  }
  static ɵfac = function DialogContentBase_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DialogContentBase)(i0.ɵɵdirectiveInject(DialogRef));
  };
  static ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
    type: DialogContentBase,
    viewQuery: function DialogContentBase_Query(rf, ctx) {
      if (rf & 1) {
        i0.ɵɵviewQuery(DialogTitleBarComponent, 5)(DialogActionsComponent, 5);
      }
      if (rf & 2) {
        let _t;
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.dialogTitleBar = _t.first);
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.dialogActions = _t.first);
      }
    }
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DialogContentBase, [{
    type: Directive
  }], () => [{
    type: DialogRef
  }], {
    dialogTitleBar: [{
      type: ViewChild,
      args: [DialogTitleBarComponent, {
        static: false
      }]
    }],
    dialogActions: [{
      type: ViewChild,
      args: [DialogActionsComponent, {
        static: false
      }]
    }]
  });
})();

/**
 * @hidden
 */
class DialogContainerService {
  static container = null;
  set container(container) {
    DialogContainerService.container = container;
  }
  get container() {
    return DialogContainerService.container;
  }
  static ɵfac = function DialogContainerService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DialogContainerService)();
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: DialogContainerService,
    factory: DialogContainerService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DialogContainerService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], null, null);
})();

/**
 * Represents the settings for Dialog actions when you open a Dialog through `DialogService`.
 *
 * Use the `DialogAction` class to configure action buttons in the Dialog. ([See example.](https://www.telerik.com/kendo-angular-ui/components/dialogs/api/dialogservice#open))
 *
 */
class DialogAction {
  /**
   * Sets the text of the action button.
   */
  text;
  /**
   * Sets the theme color of the action button. The theme color applies to the background, border, and text.
   */
  themeColor;
  /**
   * Sets the background and border styles of the action button.
   */
  fillMode;
  /**
   * Sets the CSS classes for the action button. Accepts any value supported by [`ngClass`](link:site.data.urls.angular['ngclassapi']).
   */
  cssClass;
  /**
   * Sets the [SVG icon](https://www.telerik.com/kendo-angular-ui/components/icons/svgicon/svgicon-list) to display in the action button.
   */
  svgIcon;
  /**
   * Sets the name of the [font icon](https://www.telerik.com/kendo-angular-ui/components/icons/icon/icon-list) to display in the action button.
   */
  icon;
  /**
   * @hidden
   */
  disabled;
}

/**
 * Represents the settings for opening a Dialog through the `DialogService`.
 * ([See example.](https://www.telerik.com/kendo-angular-ui/components/dialogs/api/dialogservice#open))
 *
 */
class DialogSettings {
  /**
   * Use the `preventAction` callback to check if the pressed Dialog action should be prevented. If `true`, the Dialog does not close.
   * If the **Close** button in the title bar is clicked, `DialogResult` is a `DialogCloseResult` instance.
   * If the action buttons are used to close the Dialog, `DialogResult` contains the object passed when opening the Dialog. ([See example.](https://www.telerik.com/kendo-angular-ui/components/dialogs/dialog/service#dialog-close-prevention))
   * @param {DialogResult} ev The Dialog result.
   * @param {DialogRef} [dialogRef] The Dialog reference, provided only when you create the Dialog using a component.
   * @returns Returns `true` to prevent closing the Dialog.
   */
  preventAction;
  /**
   * Sets the Dialog `title`. If you omit `title`, the Dialog does not render a **Close** button.
   */
  title;
  /**
   * Sets the CSS classes for the Dialog wrapper element. Accepts any value supported by [`ngClass`](link:site.data.urls.angular['ngclassapi']).
   */
  cssClass;
  /**
   * Configures the Dialog opening `animation` ([see example](https://www.telerik.com/kendo-angular-ui/components/dialogs/dialog/animations)).
   * @default { type: 'translate', duration: 300 }
   */
  animation;
  /**
   * Sets the HTML attributes for the Dialog wrapper element. Accepts string key-value pairs.
   *
   * @remarks
   * This option is related to accessibility.
   */
  htmlAttributes;
  /**
   * Defines the Dialog `content`. ([See example.](https://www.telerik.com/kendo-angular-ui/components/dialogs/dialog/service#rendering-the-content-area))
   */
  content;
  /**
   * Sets the width of the Dialog.
   * Use a number for pixels or a string for other units, for example, `50%`.
   */
  width;
  /**
   * Sets the minimum width of the Dialog.
   * Use a number for pixels or a string for other units, for example, `50%`.
   */
  minWidth;
  /**
   * Sets the maximum width of the Dialog.
   * Use a number for pixels or a string for other units, for example, `50%`.
   */
  maxWidth;
  /**
   * Sets the height of the Dialog.
   * Use a number for pixels or a string for other units, for example, `50%`.
   */
  height;
  /**
   * Sets the minimum height of the Dialog.
   * Use a number for pixels or a string for other units, for example, `50%`.
   */
  minHeight;
  /**
   * Sets the maximum height of the Dialog.
   * Use a number for pixels or a string for other units, for example, `50%`.
   */
  maxHeight;
  /**
   * Defines the container where the Dialog is inserted. This changes the place in the page hierarchy where the Dialog appears. The component styling stays the same.
   */
  appendTo;
  /**
   * Sets the `closeTitle` for the **Close** button.
   */
  closeTitle;
  /**
   * Specifies whether the Dialog can be closed by the **Close** button in the title bar or by pressing the `Esc` key.
   * @default true
   */
  closable;
  /**
   * Sets the Dialog `actions` buttons.
   */
  actions;
  /**
   * Sets the layout of the Dialog action buttons with `actionsLayout`.
   */
  actionsLayout;
  /**
   * Sets the query selector for the element to focus automatically with `autoFocusedElement`.
   */
  autoFocusedElement;
}

// eslint-disable max-line-length
const isNotComponent$1 = component => isString(component) || component instanceof TemplateRef;
class DialogInjector {
  getDialogRef;
  parentInjector;
  constructor(getDialogRef, parentInjector) {
    this.getDialogRef = getDialogRef;
    this.parentInjector = parentInjector;
  }
  get(token, notFoundValue) {
    if (token === DialogRef) {
      return this.getDialogRef();
    }
    return this.parentInjector.get(token, notFoundValue);
  }
}
/**
 * Provides a service for opening Dialog windows dynamically ([see example](https://www.telerik.com/kendo-angular-ui/components/dialogs/dialog/service)).
 */
class DialogService {
  containerService;
  constructor(containerService) {
    this.containerService = containerService;
  }
  /**
   * Opens a Dialog window. Requires an element in the application that uses the
   * [`kendoDialogContainer`](https://www.telerik.com/kendo-angular-ui/components/dialogs/api/dialogcontainerdirective) directive.
   * The created Dialog mounts in the DOM directly after that element.
   *
   * @param {DialogAction} options - The options that define the Dialog.
   * @returns {DialogRef} - A reference to the Dialog object and its convenience properties.
   *
   * @example
   * ```typescript
   * const dialog = this.dialogService.open({
   *   title: 'Confirm',
   *   content: 'Are you sure?',
   *   actions: [
   *     { text: 'No' },
   *     { text: 'Yes', themeColor: 'primary' }
   *   ]
   * });
   * dialog.result.subscribe(result => {
   *   // handle result
   * });
   * ```
   */
  open(options) {
    const container = options.appendTo || this.containerService.container;
    if (!container) {
      throw new Error(`
Cannot attach dialog to the page.
Add an element that uses the kendoDialogContainer directive, or set the 'appendTo' property.
See https://www.telerik.com/kendo-angular-ui/components/dialogs/dialog/service/.
          `);
    }
    // create DialogRef to (1) pass as result, (2) provide through injector
    const dialogRef = {
      close: () => {
        /* noop */
      },
      content: null,
      dialog: null,
      result: null
    };
    return this.initializeDialog(options.content, container, dialogRef, options);
  }
  initializeDialog(component, container, dialogRef, options) {
    const content = this.contentFrom(component, container, dialogRef);
    const dialog = container.createComponent(DialogComponent, {
      projectableNodes: content.nodes
    });
    dialogRef.dialog = dialog;
    dialog.changeDetectorRef.markForCheck();
    // copy @Input options to dialog instance
    this.applyOptions(dialog.instance, options);
    // create close handler and result stream
    const apiClose = new Subject();
    const close = e => {
      if (e instanceof PreventableEvent) {
        e = new DialogCloseResult();
      }
      apiClose.next(e || new DialogCloseResult());
      if (content.componentRef) {
        content.componentRef.destroy();
      }
      dialog.destroy();
    };
    const result = merge(apiClose,
    // triggered when the titlebar or actions are defined in DialogSettings
    merge(dialog.instance.close, dialog.instance.action).pipe(map(e => e instanceof PreventableEvent ? new DialogCloseResult() : e), filter(e => {
      if (options.preventAction) {
        // add dialogRef only when using component
        const dialogRefParameter = isNotComponent$1(component) ? undefined : dialogRef;
        return !options.preventAction(e, dialogRefParameter);
      }
      return true;
    }))).pipe(take(1),
    // Takes care for multiple subscriptions:
    // We subscribe internaly and the user may subscribe to get a close result - dialog.result.subscribe().
    // This causes multiple subscriptions to the same source and thus multiple emissions. share() solves that.
    share());
    result.subscribe(close);
    dialogRef.close = close;
    dialogRef.result = result;
    if (component && isDevMode()) {
      const hasContentTitle = content.nodes[0] && content.nodes[0].length > 0;
      const hasContentActions = content.nodes[2] && content.nodes[2].length > 0;
      const multipleTitles = options.title && hasContentTitle;
      const multipleActions = options.actions && hasContentActions;
      if (component.prototype instanceof DialogContentBase) {
        // content component extends DialogContentBase
        if (multipleTitles || multipleActions) {
          console.warn(`
                    Multiple Title and/or Actions configurations detected.
                    When using a component as content, provide the title and actions either in the component's markup
                    or via the title and actions properties of the DialogSettings object, but not both.
                    See https://www.telerik.com/kendo-angular-ui/components/dialogs/dialog/service/#passing-title-content-and-actions-as-a-single-component'`);
        }
      } else if (hasContentTitle || hasContentActions) {
        console.warn(`
                When Title and/or Actions markup is provided in content component's template,
                the component needs to inherit the DialogContentBase class to ensure that close and result events are properly hooked.
                See https://www.telerik.com/kendo-angular-ui/components/dialogs/dialog/service/#passing-title-content-and-actions-as-a-single-component'`);
      }
    }
    return dialogRef;
  }
  applyOptions(instance, options) {
    instance.title = options.title;
    instance.actions = options.actions;
    instance.actionsLayout = options.actionsLayout || 'stretched';
    instance.width = options.width;
    instance.minWidth = options.minWidth;
    instance.maxWidth = options.maxWidth;
    instance.height = options.height;
    instance.minHeight = options.minHeight;
    instance.maxHeight = options.maxHeight;
    instance.autoFocusedElement = options.autoFocusedElement;
    instance.closeTitle = options.closeTitle;
    instance.closable = options.closable !== undefined ? options.closable : true;
    instance.cssClass = options.cssClass;
    instance.htmlAttributes = options.htmlAttributes;
    instance.animation = options.animation !== undefined ? options.animation : true;
    if (options.content instanceof TemplateRef) {
      instance.contentTemplate = options.content;
    }
  }
  contentFrom(content, container, dialogRef) {
    const renderer = container.injector.get(Renderer2);
    let nodes = [];
    let titleNodes = [];
    let actionNodes = [];
    let componentRef = null;
    if (typeof content === 'string') {
      nodes = [renderer.createText(content)];
    } else if (content && !(content instanceof TemplateRef)) {
      // Component
      const injector = new DialogInjector(() => dialogRef, container.injector);
      componentRef = container.createComponent(content, {
        injector
      });
      titleNodes = Array.from(componentRef.location.nativeElement.querySelectorAll('kendo-dialog-titlebar'));
      nodes = [componentRef.location.nativeElement];
      actionNodes = Array.from(componentRef.location.nativeElement.querySelectorAll('kendo-dialog-actions'));
      dialogRef.content = componentRef;
    }
    return {
      componentRef,
      nodes: [titleNodes,
      // <ng-content select="kendo-dialog-titlebar">
      nodes,
      // <ng-content>
      actionNodes // <ng-content select="kendo-dialog-actions">
      ]
    };
  }
  static ɵfac = function DialogService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DialogService)(i0.ɵɵinject(DialogContainerService));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: DialogService,
    factory: DialogService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DialogService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: DialogContainerService,
    decorators: [{
      type: Inject,
      args: [DialogContainerService]
    }]
  }], null);
})();

/**
 * @hidden
 */
let newZIndex = 11510;
/**
 * @hidden
 */
const DEFAULT_OPTIONS = {
  draggable: true,
  height: null,
  left: null,
  minHeight: 100,
  minWidth: 120,
  position: 'absolute',
  resizable: true,
  state: 'default',
  top: null,
  width: null
};
/**
 * @hidden
 */
const createMoveStream = (el, ev) => mouseDown => {
  return el.kendoDrag.pipe(takeUntil(el.kendoRelease.pipe(tap(() => {
    ev.emit();
  }))), map(({
    pageX,
    pageY
  }) => ({
    originalX: mouseDown.pageX,
    originalY: mouseDown.pageY,
    pageX,
    pageY
  })));
};
/**
 * @hidden
 */
class DragResizeService {
  ngZone;
  close = new EventEmitter();
  focus = new EventEmitter();
  change = new EventEmitter();
  stateChange = new EventEmitter();
  dragStart = new EventEmitter();
  dragEnd = new EventEmitter();
  resizeStart = new EventEmitter();
  resizeEnd = new EventEmitter();
  options = Object.assign({}, DEFAULT_OPTIONS);
  restoreOptions;
  window;
  lastAction = null;
  subscriptions = new Subscription();
  dragSubscription = new Subscription();
  constructor(ngZone) {
    this.ngZone = ngZone;
  }
  ngOnDestroy() {
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
    if (this.dragSubscription) {
      this.dragSubscription.unsubscribe();
    }
  }
  init(el) {
    const state = this.options.state;
    const options = this.options;
    this.window = el;
    if (state !== 'default' && !isPresent(this.restoreOptions)) {
      this.restoreOptions = Object.assign({}, options);
    }
    if (state === 'maximized') {
      options.position = 'fixed';
    }
  }
  onDrag(el) {
    this.subscriptions.add(this.ngZone.runOutsideAngular(() => {
      let startPosition;
      let dragStarted;
      this.dragSubscription = el.kendoPress.pipe(tap(ev => {
        if (!ev.isTouch) {
          preventDefault(ev);
        }
        this.focus.emit();
        startPosition = this.currentPosition();
        dragStarted = false;
      }), switchMap(createMoveStream(el, this.dragEnd))).subscribe(({
        pageX,
        pageY,
        originalX,
        originalY
      }) => {
        if (!dragStarted) {
          this.ensureWidth();
          this.dragStart.emit();
          dragStarted = true;
        }
        this.handleDrag({
          originalX,
          originalY,
          pageX,
          pageY,
          startPosition
        });
      });
    }));
  }
  handleDrag({
    originalX,
    originalY,
    pageX,
    pageY,
    startPosition
  }) {
    this.options.left = startPosition.x + pageX - originalX;
    this.options.top = startPosition.y + pageY - originalY;
    if (this.options.state === 'minimized' && isPresent(this.restoreOptions)) {
      this.restoreOptions.left = this.options.left;
      this.restoreOptions.top = this.options.top;
    }
    this.change.emit({
      left: startPosition.x + pageX - originalX,
      top: startPosition.y + pageY - originalY
    });
  }
  onResize(handle, direction) {
    this.subscriptions.add(this.ngZone.runOutsideAngular(() => {
      let startOffsetAndPosition;
      let resizeStarted = false;
      handle.kendoPress.pipe(tap(ev => {
        preventDefault(ev);
        this.focus.emit();
        startOffsetAndPosition = this.currentOffsetAndPosition();
        resizeStarted = false;
      }), switchMap(createMoveStream(handle, this.resizeEnd))).subscribe(({
        pageX,
        pageY,
        originalX,
        originalY
      }) => {
        if (!resizeStarted) {
          this.resizeStart.emit(direction);
          resizeStarted = true;
        }
        const deltaX = pageX - originalX;
        const deltaY = pageY - originalY;
        this.handleResize(startOffsetAndPosition, direction, deltaX, deltaY);
      });
    }));
  }
  handleResize(initial, dir, deltaX, deltaY) {
    const old = this.options;
    const ev = {};
    if (dir.indexOf('e') >= 0) {
      const newWidth = initial.width + deltaX;
      if (newWidth !== old.width && newWidth >= old.minWidth) {
        ev.width = newWidth;
      }
    }
    if (dir.indexOf('n') >= 0) {
      const newHeight = initial.height - deltaY;
      const newTop = initial.y + deltaY;
      if (newHeight !== old.height && newHeight >= old.minHeight && newTop !== old.top) {
        ev.height = newHeight;
        ev.top = newTop;
      }
    }
    if (dir.indexOf('s') >= 0) {
      const newHeight = initial.height + deltaY;
      if (newHeight !== old.height && newHeight >= old.minHeight) {
        ev.height = newHeight;
      }
    }
    if (dir.indexOf('w') >= 0) {
      const newLeft = initial.x + deltaX;
      const newWidth = initial.width - deltaX;
      if (newWidth !== old.width && newWidth >= old.minWidth && newLeft !== old.left) {
        ev.width = newWidth;
        ev.left = newLeft;
      }
    }
    if (isPresent(ev.width) || isPresent(ev.height)) {
      OFFSET_STYLES.forEach(style => {
        if (isPresent(ev[style])) {
          this.options[style] = ev[style];
        }
      });
      this.change.emit(ev);
    }
  }
  restoreAction() {
    this.lastAction = 'restore';
    this.defaultState();
  }
  defaultState() {
    if (isPresent(this.restoreOptions)) {
      this.options = Object.assign({}, this.restoreOptions);
    }
    this.options.state = 'default';
    this.ngZone.onStable.pipe(take(1)).subscribe(() => {
      if (!isPresent(this.options.left) || !isPresent(this.options.top)) {
        this.center();
      }
    });
    this.stateChange.emit('default');
  }
  storeOptions() {
    this.restoreOptions = Object.assign({}, this.options);
  }
  maximizeAction() {
    this.lastAction = 'maximize';
    this.maximizeState();
  }
  maximizeState() {
    this.storeOptions();
    const wnd = this.windowViewPort;
    this.options = Object.assign({}, this.options, {
      height: wnd.height,
      left: 0,
      position: 'fixed',
      state: 'maximized',
      top: 0,
      width: wnd.width
    });
    this.stateChange.emit('maximized');
  }
  minimizeAction() {
    this.lastAction = 'minimize';
    this.minimizeState();
  }
  minimizeState() {
    this.storeOptions();
    this.options = Object.assign({}, this.options, {
      height: null,
      position: 'absolute',
      state: 'minimized'
    });
    this.stateChange.emit('minimized');
  }
  /**
   * Handles manual changes of the 'state' property.
   * Required to distinguish them from action clicks.
   */
  applyManualState(previousState) {
    const state = this.options.state;
    switch (state) {
      case 'default':
        this.defaultState();
        break;
      case 'maximized':
        this.maximizeState();
        break;
      case 'minimized':
        if (previousState) {
          this.options.state = previousState;
        }
        this.minimizeState();
        break;
      default:
        break;
    }
  }
  closeAction() {
    this.close.emit();
  }
  ensureWidth() {
    const windowOffset = offset(this.window.nativeElement);
    if (!isPresent(this.options.width)) {
      this.options.width = windowOffset.width;
      this.change.emit({
        width: windowOffset.width
      });
    }
  }
  center() {
    if (this.options.state === 'maximized') {
      return;
    }
    const wnd = this.windowViewPort;
    const wrapper = offset(this.window.nativeElement);
    const el = this.window.nativeElement;
    const ev = {};
    if (!isPresent(this.options.left)) {
      const targetX = Math.max(0, (wnd.width - wrapper.width) / 2);
      this.options.left = el.offsetLeft + targetX - wrapper.left;
      ev.left = this.options.left;
    }
    if (!isPresent(this.options.top)) {
      const targetY = Math.max(0, (wnd.height - wrapper.height) / 2);
      this.options.top = el.offsetTop + targetY - wrapper.top;
      ev.top = this.options.top;
    }
    this.change.emit(ev);
  }
  currentOffsetAndPosition() {
    const o = this.options;
    const off = offset(this.window.nativeElement);
    return Object.assign({}, this.currentPosition(), {
      height: o.height ? o.height : off.height,
      width: o.width ? o.width : off.width
    });
  }
  currentPosition() {
    const o = this.options;
    if (!isPresent(o.top) || !isPresent(o.left)) {
      this.setPosition();
    }
    return {
      x: this.options.left,
      y: this.options.top
    };
  }
  setPosition() {
    this.options.left = this.window.nativeElement.offsetLeft;
    this.options.top = this.window.nativeElement.offsetTop;
  }
  setRestoreOption(style, value) {
    if (isPresent(this.restoreOptions)) {
      this.restoreOptions[style] = value;
    }
  }
  get nextPossibleZIndex() {
    return newZIndex;
  }
  get nextZIndex() {
    return newZIndex++;
  }
  get windowViewPort() {
    return getWindowViewPort(this.window.nativeElement);
  }
  static ɵfac = function DragResizeService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DragResizeService)(i0.ɵɵinject(i0.NgZone));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: DragResizeService,
    factory: DragResizeService.ɵfac
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DragResizeService, [{
    type: Injectable
  }], () => [{
    type: i0.NgZone
  }], null);
})();

/**
 * @hidden
 */
class ResizeHandleDirective {
  draggable;
  el;
  renderer;
  service;
  direction;
  get hostClass() {
    return true;
  }
  subscriptions = new Subscription();
  constructor(draggable, el, renderer, service) {
    this.draggable = draggable;
    this.el = el;
    this.renderer = renderer;
    this.service = service;
  }
  ngOnInit() {
    this.setDisplay();
    this.renderer.addClass(this.el.nativeElement, 'k-resize-' + this.direction);
    this.subscriptions.add(of(this.draggable).subscribe(handle => {
      this.service.onResize(handle, this.direction);
    }));
    this.subscriptions.add(this.service.resizeStart.subscribe(dir => {
      if (dir !== this.direction) {
        this.setDisplay('none');
      }
    }));
    this.subscriptions.add(this.service.dragStart.subscribe(() => {
      this.setDisplay('none');
    }));
    this.subscriptions.add(merge(this.service.resizeEnd, this.service.dragEnd).subscribe(() => {
      this.setDisplay('block');
    }));
    this.subscriptions.add(this.service.stateChange.subscribe(state => {
      this.setDisplay(state === 'default' ? 'block' : 'none');
    }));
  }
  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
  setDisplay(value = 'block') {
    this.renderer.setStyle(this.el.nativeElement, 'display', this.service.options.state === 'default' ? value : 'none');
  }
  static ɵfac = function ResizeHandleDirective_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || ResizeHandleDirective)(i0.ɵɵdirectiveInject(i1$2.DraggableDirective, 1), i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.Renderer2), i0.ɵɵdirectiveInject(DragResizeService));
  };
  static ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
    type: ResizeHandleDirective,
    selectors: [["", "kendoWindowResizeHandle", ""]],
    hostVars: 2,
    hostBindings: function ResizeHandleDirective_HostBindings(rf, ctx) {
      if (rf & 2) {
        i0.ɵɵclassProp("k-resize-handle", ctx.hostClass);
      }
    },
    inputs: {
      direction: "direction"
    }
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ResizeHandleDirective, [{
    type: Directive,
    args: [{
      selector: '[kendoWindowResizeHandle]',
      standalone: true
    }]
  }], () => [{
    type: i1$2.DraggableDirective,
    decorators: [{
      type: Host
    }]
  }, {
    type: i0.ElementRef
  }, {
    type: i0.Renderer2
  }, {
    type: DragResizeService
  }], {
    direction: [{
      type: Input
    }],
    hostClass: [{
      type: HostBinding,
      args: ['class.k-resize-handle']
    }]
  });
})();

/**
 * Represents the title bar of the Window component.
 *
 */
class WindowTitleBarComponent {
  el;
  ngZone;
  /**
   * @hidden
   */
  template;
  /**
   * @hidden
   */
  id;
  /**
   * @hidden
   */
  service;
  dragDirective;
  dragSubscription;
  stateSubscription;
  constructor(el, service, ngZone) {
    this.el = el;
    this.ngZone = ngZone;
    this.service = service;
  }
  ngOnInit() {
    this.dragDirective = new DraggableDirective(this.el, this.ngZone);
    this.dragDirective.ngOnInit();
    if (this.isDraggable) {
      this.subscribeDrag();
    }
    this.subscribeStateChange();
  }
  ngAfterViewInit() {
    if (!this.id || this.template) {
      return;
    }
    this.ngZone.onStable.pipe(take(1)).subscribe(() => {
      const element = this.el.nativeElement.querySelector('.k-window-title');
      element?.setAttribute('id', this.id);
    });
  }
  ngOnDestroy() {
    this.dragDirective.ngOnDestroy();
    this.unsubscribeDrag();
    this.unsubscribeState();
  }
  /**
   * @hidden
   */
  subscribeDrag() {
    this.unsubscribeDrag();
    this.dragSubscription = of(this.dragDirective).subscribe(titleBar => {
      this.service.onDrag(titleBar);
    });
  }
  /**
   * @hidden
   */
  subscribeStateChange() {
    this.stateSubscription = this.service.stateChange.subscribe(state => {
      if (this.service.options.draggable) {
        if (state === 'maximized') {
          this.unsubscribeDrag();
        } else {
          this.subscribeDrag();
        }
      }
    });
  }
  /**
   * @hidden
   */
  unsubscribeDrag() {
    if (this.dragSubscription) {
      this.service.dragSubscription.unsubscribe();
      this.dragSubscription.unsubscribe();
      this.dragSubscription = null;
    }
  }
  /**
   * @hidden
   */
  unsubscribeState() {
    if (this.stateSubscription) {
      this.stateSubscription.unsubscribe();
      this.stateSubscription = null;
    }
  }
  get className() {
    return true;
  }
  get touchAction() {
    if (this.isDraggable) {
      return 'none';
    }
  }
  /**
   * @hidden
   */
  handle(ev) {
    const target = ev.target;
    const state = this.service.options.state;
    if (!hasClasses(target, 'k-icon') && !isFocusable(target, false) && this.service.options.resizable) {
      if (state === 'default') {
        this.service.maximizeAction();
      } else if (state === 'maximized') {
        this.service.restoreAction();
      }
    }
  }
  get isDraggable() {
    const options = this.service.options;
    return options.draggable && options.state !== 'maximized';
  }
  static ɵfac = function WindowTitleBarComponent_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || WindowTitleBarComponent)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(DragResizeService), i0.ɵɵdirectiveInject(i0.NgZone));
  };
  static ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
    type: WindowTitleBarComponent,
    selectors: [["kendo-window-titlebar"]],
    hostVars: 4,
    hostBindings: function WindowTitleBarComponent_HostBindings(rf, ctx) {
      if (rf & 1) {
        i0.ɵɵlistener("dblclick", function WindowTitleBarComponent_dblclick_HostBindingHandler($event) {
          return ctx.handle($event);
        });
      }
      if (rf & 2) {
        i0.ɵɵstyleProp("touch-action", ctx.touchAction);
        i0.ɵɵclassProp("k-window-titlebar", ctx.className);
      }
    },
    inputs: {
      template: "template",
      id: "id"
    },
    ngContentSelectors: _c0,
    decls: 2,
    vars: 2,
    consts: [[3, "ngTemplateOutlet", "ngTemplateOutletContext"]],
    template: function WindowTitleBarComponent_Template(rf, ctx) {
      if (rf & 1) {
        i0.ɵɵprojectionDef();
        i0.ɵɵconditionalCreate(0, WindowTitleBarComponent_Conditional_0_Template, 1, 0);
        i0.ɵɵconditionalCreate(1, WindowTitleBarComponent_Conditional_1_Template, 1, 4, null, 0);
      }
      if (rf & 2) {
        i0.ɵɵconditional(!ctx.template ? 0 : -1);
        i0.ɵɵadvance();
        i0.ɵɵconditional(ctx.template ? 1 : -1);
      }
    },
    dependencies: [NgTemplateOutlet],
    encapsulation: 2
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(WindowTitleBarComponent, [{
    type: Component,
    args: [{
      selector: 'kendo-window-titlebar',
      template: `
	@if (!template) {
	  <ng-content></ng-content>
	}
	@if (template) {
	  <ng-template
	    [ngTemplateOutlet]="template"
	    [ngTemplateOutletContext]="{'$implicit': service}">
	  </ng-template>
	}
	`,
      standalone: true,
      imports: [NgTemplateOutlet]
    }]
  }], () => [{
    type: i0.ElementRef
  }, {
    type: DragResizeService
  }, {
    type: i0.NgZone
  }], {
    template: [{
      type: Input
    }],
    id: [{
      type: Input
    }],
    className: [{
      type: HostBinding,
      args: ['class.k-window-titlebar']
    }],
    touchAction: [{
      type: HostBinding,
      args: ['style.touch-action']
    }],
    handle: [{
      type: HostListener,
      args: ['dblclick', ['$event']]
    }]
  });
})();

/**
 * @hidden
 */
class NavigationService {
  window;
  ngZone;
  constructor(window, ngZone) {
    this.window = window;
    this.ngZone = ngZone;
  }
  process(ev) {
    const key = normalizeKeys(ev);
    const target = ev.target;
    switch (key) {
      case Keys.ArrowUp:
      case Keys.ArrowDown:
      case Keys.ArrowLeft:
      case Keys.ArrowRight:
        {
          if (hasClasses(target, WINDOW_CLASSES)) {
            ev.preventDefault();
            this.ngZone.run(() => {
              this.handleArrow(key, ev);
            });
          }
          break;
        }
      case Keys.Escape:
        if (hasClasses(target, WINDOW_ELEMENTS_HANDLING_ESC_KEY)) {
          this.ngZone.run(() => {
            this.handleEscape();
          });
        }
        break;
      default:
        break;
    }
  }
  handleArrow(key, ev) {
    const options = this.window.options;
    if (ev.altKey) {
      this.handleStateChange(key, options.state);
      return;
    }
    if ((ev.ctrlKey || ev.metaKey) && options.state === 'default') {
      this.handleResize(key);
    } else {
      this.handleDrag(key);
    }
  }
  handleEscape() {
    this.window.closeAction();
  }
  handleDrag(key) {
    const options = this.window.options;
    if (!options.draggable) {
      return;
    }
    const offset = this.window.currentOffsetAndPosition();
    const restoreOptions = this.window.restoreOptions;
    const ev = {};
    let delta = 10;
    if (key === Keys.ArrowLeft || key === Keys.ArrowUp) {
      delta *= -1;
    }
    switch (key) {
      case Keys.ArrowLeft:
      case Keys.ArrowRight:
        {
          ev.left = offset.x + delta;
          options.left = ev.left;
          break;
        }
      case Keys.ArrowUp:
      case Keys.ArrowDown:
        {
          ev.top = offset.y + delta;
          options.top = ev.top;
          break;
        }
      default:
        break;
    }
    if (options.state === 'minimized' && isPresent(restoreOptions)) {
      restoreOptions.left = options.left;
      restoreOptions.top = options.top;
    }
    this.window.change.emit(ev);
  }
  handleResize(key) {
    const options = this.window.options;
    if (!options.resizable) {
      return;
    }
    const offset = this.window.currentOffsetAndPosition();
    let newWidth;
    let newHeight;
    const ev = {};
    let delta = 10;
    if (key === Keys.ArrowLeft || key === Keys.ArrowUp) {
      delta *= -1;
    }
    switch (key) {
      case Keys.ArrowLeft:
      case Keys.ArrowRight:
        {
          newWidth = offset.width + delta;
          if (newWidth !== options.width && newWidth >= options.minWidth) {
            ev.width = newWidth;
          }
          break;
        }
      case Keys.ArrowUp:
      case Keys.ArrowDown:
        {
          newHeight = offset.height + delta;
          if (newHeight !== options.height && newHeight >= options.minHeight) {
            ev.height = newHeight;
          }
          break;
        }
      default:
        break;
    }
    if (isPresent(ev.width) || isPresent(ev.height)) {
      OFFSET_STYLES.forEach(style => {
        if (isPresent(ev[style])) {
          this.window.options[style] = ev[style];
        }
      });
      this.window.change.emit(ev);
    }
  }
  handleStateChange(key, state) {
    if (state === 'minimized' && key === Keys.ArrowUp || state === 'maximized' && key === Keys.ArrowDown) {
      this.window.restoreAction();
      return;
    }
    if (state === 'default') {
      if (key === Keys.ArrowUp) {
        this.window.maximizeAction();
      } else if (key === Keys.ArrowDown) {
        this.window.minimizeAction();
      }
    }
  }
  static ɵfac = function NavigationService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || NavigationService)(i0.ɵɵinject(DragResizeService), i0.ɵɵinject(i0.NgZone));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: NavigationService,
    factory: NavigationService.ɵfac
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NavigationService, [{
    type: Injectable
  }], () => [{
    type: DragResizeService
  }, {
    type: i0.NgZone
  }], null);
})();

/**
 * Represents the close action directive of the Window component.
 *
 * ```html
 * <button kendoWindowCloseAction>Close</button>
 * ```
 */
class WindowCloseActionDirective extends Button {
  /**
   * @hidden
   */
  window;
  /**
   * @hidden
   */
  xIcon = xIcon;
  buttonType = 'button';
  buttonClass = true;
  constructor(el, renderer, _service, localization, ngZone) {
    super(el, renderer, null, localization, ngZone);
    this.window = _service;
    this.fillMode = 'flat';
    this.size = 'xsmall';
    this.icon = 'x';
  }
  /**
   * @hidden
   */
  onClick() {
    if (!this.isDisabled) {
      this.window.closeAction();
    }
  }
  static ɵfac = function WindowCloseActionDirective_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || WindowCloseActionDirective)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.Renderer2), i0.ɵɵdirectiveInject(DragResizeService, 8), i0.ɵɵdirectiveInject(i1$1.LocalizationService), i0.ɵɵdirectiveInject(i0.NgZone));
  };
  static ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
    type: WindowCloseActionDirective,
    selectors: [["button", "kendoWindowCloseAction", ""]],
    hostVars: 3,
    hostBindings: function WindowCloseActionDirective_HostBindings(rf, ctx) {
      if (rf & 1) {
        i0.ɵɵlistener("click", function WindowCloseActionDirective_click_HostBindingHandler() {
          return ctx.onClick();
        });
      }
      if (rf & 2) {
        i0.ɵɵattribute("type", ctx.buttonType);
        i0.ɵɵclassProp("k-window-titlebar-action", ctx.buttonClass);
      }
    },
    inputs: {
      window: "window"
    },
    exportAs: ["kendoWindowCloseAction"],
    features: [i0.ɵɵProvidersFeature([LocalizationService, {
      provide: L10N_PREFIX,
      useValue: 'kendo.button'
    }]), i0.ɵɵInheritDefinitionFeature],
    attrs: _c5,
    ngContentSelectors: _c0,
    decls: 5,
    vars: 3,
    consts: [["innerCssClass", "k-button-icon", "name", "close", 3, "svgIcon"], [1, "k-button-icon", "k-icon"], [1, "k-button-icon", 3, "ngClass"], [1, "k-button-text"], ["role", "presentation", 1, "k-image", 3, "src"]],
    template: function WindowCloseActionDirective_Template(rf, ctx) {
      if (rf & 1) {
        i0.ɵɵprojectionDef();
        i0.ɵɵconditionalCreate(0, WindowCloseActionDirective_Conditional_0_Template, 1, 1, "kendo-icon-wrapper", 0);
        i0.ɵɵconditionalCreate(1, WindowCloseActionDirective_Conditional_1_Template, 2, 1, "span", 1);
        i0.ɵɵconditionalCreate(2, WindowCloseActionDirective_Conditional_2_Template, 1, 1, "span", 2);
        i0.ɵɵelementStart(3, "span", 3);
        i0.ɵɵprojection(4);
        i0.ɵɵelementEnd();
      }
      if (rf & 2) {
        i0.ɵɵconditional(!ctx.imageUrl && !ctx.iconClass ? 0 : -1);
        i0.ɵɵadvance();
        i0.ɵɵconditional(ctx.imageUrl ? 1 : -1);
        i0.ɵɵadvance();
        i0.ɵɵconditional(ctx.iconClass ? 2 : -1);
      }
    },
    dependencies: [IconWrapperComponent, NgClass],
    encapsulation: 2
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(WindowCloseActionDirective, [{
    type: Component,
    args: [{
      exportAs: 'kendoWindowCloseAction',
      providers: [LocalizationService, {
        provide: L10N_PREFIX,
        useValue: 'kendo.button'
      }],
      selector: 'button[kendoWindowCloseAction]',
      template: `
        @if (!imageUrl && !iconClass) {
          <kendo-icon-wrapper
            innerCssClass="k-button-icon"
            name="close"
            [svgIcon]="xIcon">
          </kendo-icon-wrapper>
        }
        @if (imageUrl) {
          <span class="k-button-icon k-icon">
            <img [src]="imageUrl" class="k-image" role="presentation" />
          </span>
        }
        @if (iconClass) {
          <span class="k-button-icon" [ngClass]="iconClass"></span>
        }
        <span class="k-button-text"><ng-content></ng-content></span>
        `,
      standalone: true,
      imports: [IconWrapperComponent, NgClass]
    }]
  }], () => [{
    type: i0.ElementRef
  }, {
    type: i0.Renderer2
  }, {
    type: DragResizeService,
    decorators: [{
      type: Optional
    }]
  }, {
    type: i1$1.LocalizationService
  }, {
    type: i0.NgZone
  }], {
    window: [{
      type: Input
    }],
    buttonType: [{
      type: HostBinding,
      args: ['attr.type']
    }],
    buttonClass: [{
      type: HostBinding,
      args: ['class.k-window-titlebar-action']
    }],
    onClick: [{
      type: HostListener,
      args: ['click']
    }]
  });
})();

/**
 * Represents the restore action directive of the Window component.
 *
 * ```html
 * <button kendoWindowRestoreAction>Restore</button>
 * ```
 */
class WindowRestoreActionDirective extends Button {
  /**
   * @hidden
   */
  window;
  /**
   * @hidden
   */
  windowRestoreIcon = windowRestoreIcon;
  buttonType = 'button';
  buttonClass = true;
  constructor(el, renderer, _service, localization, ngZone) {
    super(el, renderer, null, localization, ngZone);
    this.window = _service;
    this.fillMode = 'flat';
    this.size = 'xsmall';
    this.icon = 'window-restore';
  }
  /**
   * @hidden
   */
  onClick() {
    if (!this.isDisabled) {
      this.window.restoreAction();
    }
  }
  get visible() {
    return this.window.options.state === 'default' ? 'none' : 'inline-flex';
  }
  static ɵfac = function WindowRestoreActionDirective_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || WindowRestoreActionDirective)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.Renderer2), i0.ɵɵdirectiveInject(DragResizeService, 8), i0.ɵɵdirectiveInject(i1$1.LocalizationService), i0.ɵɵdirectiveInject(i0.NgZone));
  };
  static ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
    type: WindowRestoreActionDirective,
    selectors: [["button", "kendoWindowRestoreAction", ""]],
    hostVars: 5,
    hostBindings: function WindowRestoreActionDirective_HostBindings(rf, ctx) {
      if (rf & 1) {
        i0.ɵɵlistener("click", function WindowRestoreActionDirective_click_HostBindingHandler() {
          return ctx.onClick();
        });
      }
      if (rf & 2) {
        i0.ɵɵattribute("type", ctx.buttonType);
        i0.ɵɵstyleProp("display", ctx.visible);
        i0.ɵɵclassProp("k-window-titlebar-action", ctx.buttonClass);
      }
    },
    inputs: {
      window: "window"
    },
    exportAs: ["kendoWindowRestoreAction"],
    features: [i0.ɵɵProvidersFeature([LocalizationService, {
      provide: L10N_PREFIX,
      useValue: 'kendo.button'
    }]), i0.ɵɵInheritDefinitionFeature],
    attrs: _c6,
    ngContentSelectors: _c0,
    decls: 5,
    vars: 3,
    consts: [["innerCssClass", "k-button-icon", "name", "window-restore", 3, "svgIcon"], [1, "k-button-icon", "k-icon"], [1, "k-button-icon", 3, "ngClass"], [1, "k-button-text"], ["role", "presentation", 1, "k-image", 3, "src"]],
    template: function WindowRestoreActionDirective_Template(rf, ctx) {
      if (rf & 1) {
        i0.ɵɵprojectionDef();
        i0.ɵɵconditionalCreate(0, WindowRestoreActionDirective_Conditional_0_Template, 1, 1, "kendo-icon-wrapper", 0);
        i0.ɵɵconditionalCreate(1, WindowRestoreActionDirective_Conditional_1_Template, 2, 1, "span", 1);
        i0.ɵɵconditionalCreate(2, WindowRestoreActionDirective_Conditional_2_Template, 1, 1, "span", 2);
        i0.ɵɵelementStart(3, "span", 3);
        i0.ɵɵprojection(4);
        i0.ɵɵelementEnd();
      }
      if (rf & 2) {
        i0.ɵɵconditional(!ctx.imageUrl && !ctx.iconClass ? 0 : -1);
        i0.ɵɵadvance();
        i0.ɵɵconditional(ctx.imageUrl ? 1 : -1);
        i0.ɵɵadvance();
        i0.ɵɵconditional(ctx.iconClass ? 2 : -1);
      }
    },
    dependencies: [IconWrapperComponent, NgClass],
    encapsulation: 2
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(WindowRestoreActionDirective, [{
    type: Component,
    args: [{
      exportAs: 'kendoWindowRestoreAction',
      providers: [LocalizationService, {
        provide: L10N_PREFIX,
        useValue: 'kendo.button'
      }],
      selector: 'button[kendoWindowRestoreAction]',
      template: `
        @if (!imageUrl && !iconClass) {
          <kendo-icon-wrapper
            innerCssClass="k-button-icon"
            name="window-restore"
            [svgIcon]="windowRestoreIcon">
          </kendo-icon-wrapper>
        }
        @if (imageUrl) {
          <span class="k-button-icon k-icon">
            <img [src]="imageUrl" class="k-image" role="presentation" />
          </span>
        }
        @if (iconClass) {
          <span class="k-button-icon" [ngClass]="iconClass"></span>
        }
        <span class="k-button-text"><ng-content></ng-content></span>
        `,
      standalone: true,
      imports: [IconWrapperComponent, NgClass]
    }]
  }], () => [{
    type: i0.ElementRef
  }, {
    type: i0.Renderer2
  }, {
    type: DragResizeService,
    decorators: [{
      type: Optional
    }]
  }, {
    type: i1$1.LocalizationService
  }, {
    type: i0.NgZone
  }], {
    window: [{
      type: Input
    }],
    buttonType: [{
      type: HostBinding,
      args: ['attr.type']
    }],
    buttonClass: [{
      type: HostBinding,
      args: ['class.k-window-titlebar-action']
    }],
    onClick: [{
      type: HostListener,
      args: ['click']
    }],
    visible: [{
      type: HostBinding,
      args: ['style.display']
    }]
  });
})();

/**
 * Represents the maximize action directive of the Window component.
 *
 * ```html
 * <button kendoWindowMaximizeAction>Maximize</button>
 * ```
 */
class WindowMaximizeActionDirective extends Button {
  /**
   * @hidden
   */
  window;
  buttonType = 'button';
  buttonClass = true;
  /**
   * @hidden
   */
  windowIcon = windowIcon;
  constructor(el, renderer, _service, localization, ngZone) {
    super(el, renderer, null, localization, ngZone);
    this.window = _service;
    this.fillMode = 'flat';
    this.size = 'xsmall';
    this.icon = 'window';
  }
  /**
   * @hidden
   */
  onClick() {
    if (!this.isDisabled) {
      this.window.maximizeAction();
    }
  }
  get visible() {
    return this.window.options.state === 'default' ? 'inline-flex' : 'none';
  }
  static ɵfac = function WindowMaximizeActionDirective_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || WindowMaximizeActionDirective)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.Renderer2), i0.ɵɵdirectiveInject(DragResizeService, 8), i0.ɵɵdirectiveInject(i1$1.LocalizationService), i0.ɵɵdirectiveInject(i0.NgZone));
  };
  static ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
    type: WindowMaximizeActionDirective,
    selectors: [["button", "kendoWindowMaximizeAction", ""]],
    hostVars: 5,
    hostBindings: function WindowMaximizeActionDirective_HostBindings(rf, ctx) {
      if (rf & 1) {
        i0.ɵɵlistener("click", function WindowMaximizeActionDirective_click_HostBindingHandler() {
          return ctx.onClick();
        });
      }
      if (rf & 2) {
        i0.ɵɵattribute("type", ctx.buttonType);
        i0.ɵɵstyleProp("display", ctx.visible);
        i0.ɵɵclassProp("k-window-titlebar-action", ctx.buttonClass);
      }
    },
    inputs: {
      window: "window"
    },
    exportAs: ["kendoWindowMaximizeAction"],
    features: [i0.ɵɵProvidersFeature([LocalizationService, {
      provide: L10N_PREFIX,
      useValue: 'kendo.button'
    }]), i0.ɵɵInheritDefinitionFeature],
    attrs: _c7,
    ngContentSelectors: _c0,
    decls: 5,
    vars: 3,
    consts: [["innerCssClass", "k-button-icon", "name", "window", 3, "svgIcon"], [1, "k-button-icon", "k-icon"], [1, "k-button-icon", 3, "ngClass"], [1, "k-button-text"], ["role", "presentation", 1, "k-image", 3, "src"]],
    template: function WindowMaximizeActionDirective_Template(rf, ctx) {
      if (rf & 1) {
        i0.ɵɵprojectionDef();
        i0.ɵɵconditionalCreate(0, WindowMaximizeActionDirective_Conditional_0_Template, 1, 1, "kendo-icon-wrapper", 0);
        i0.ɵɵconditionalCreate(1, WindowMaximizeActionDirective_Conditional_1_Template, 2, 1, "span", 1);
        i0.ɵɵconditionalCreate(2, WindowMaximizeActionDirective_Conditional_2_Template, 1, 1, "span", 2);
        i0.ɵɵelementStart(3, "span", 3);
        i0.ɵɵprojection(4);
        i0.ɵɵelementEnd();
      }
      if (rf & 2) {
        i0.ɵɵconditional(!ctx.imageUrl && !ctx.iconClass ? 0 : -1);
        i0.ɵɵadvance();
        i0.ɵɵconditional(ctx.imageUrl ? 1 : -1);
        i0.ɵɵadvance();
        i0.ɵɵconditional(ctx.iconClass ? 2 : -1);
      }
    },
    dependencies: [IconWrapperComponent, NgClass],
    encapsulation: 2
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(WindowMaximizeActionDirective, [{
    type: Component,
    args: [{
      exportAs: 'kendoWindowMaximizeAction',
      providers: [LocalizationService, {
        provide: L10N_PREFIX,
        useValue: 'kendo.button'
      }],
      selector: 'button[kendoWindowMaximizeAction]',
      template: `
        @if (!imageUrl && !iconClass) {
          <kendo-icon-wrapper
            innerCssClass="k-button-icon"
            name="window"
            [svgIcon]="windowIcon">
          </kendo-icon-wrapper>
        }
        @if (imageUrl) {
          <span class="k-button-icon k-icon">
            <img [src]="imageUrl" class="k-image" role="presentation" />
          </span>
        }
        @if (iconClass) {
          <span class="k-button-icon" [ngClass]="iconClass"></span>
        }
        <span class="k-button-text"><ng-content></ng-content></span>
        `,
      standalone: true,
      imports: [IconWrapperComponent, NgClass]
    }]
  }], () => [{
    type: i0.ElementRef
  }, {
    type: i0.Renderer2
  }, {
    type: DragResizeService,
    decorators: [{
      type: Optional
    }]
  }, {
    type: i1$1.LocalizationService
  }, {
    type: i0.NgZone
  }], {
    window: [{
      type: Input
    }],
    buttonType: [{
      type: HostBinding,
      args: ['attr.type']
    }],
    buttonClass: [{
      type: HostBinding,
      args: ['class.k-window-titlebar-action']
    }],
    onClick: [{
      type: HostListener,
      args: ['click']
    }],
    visible: [{
      type: HostBinding,
      args: ['style.display']
    }]
  });
})();

/**
 * Represents the minimize action directive of the Window component.
 *
 * ```html
 * <button kendoWindowMinimizeAction>Minimize</button>
 * ```
 */
class WindowMinimizeActionDirective extends Button {
  /**
   * @hidden
   */
  window;
  buttonType = 'button';
  buttonClass = true;
  /**
   * @hidden
   */
  windowMinimizeIcon = windowMinimizeIcon;
  constructor(el, renderer, _service, localization, ngZone) {
    super(el, renderer, null, localization, ngZone);
    this.window = _service;
    this.fillMode = 'flat';
    this.size = 'xsmall';
    this.icon = 'window-minimize';
  }
  /**
   * @hidden
   */
  onClick() {
    if (!this.isDisabled) {
      this.window.minimizeAction();
    }
  }
  get visible() {
    return this.window.options.state === 'default' ? 'inline-flex' : 'none';
  }
  static ɵfac = function WindowMinimizeActionDirective_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || WindowMinimizeActionDirective)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.Renderer2), i0.ɵɵdirectiveInject(DragResizeService, 8), i0.ɵɵdirectiveInject(i1$1.LocalizationService), i0.ɵɵdirectiveInject(i0.NgZone));
  };
  static ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
    type: WindowMinimizeActionDirective,
    selectors: [["button", "kendoWindowMinimizeAction", ""]],
    hostVars: 5,
    hostBindings: function WindowMinimizeActionDirective_HostBindings(rf, ctx) {
      if (rf & 1) {
        i0.ɵɵlistener("click", function WindowMinimizeActionDirective_click_HostBindingHandler() {
          return ctx.onClick();
        });
      }
      if (rf & 2) {
        i0.ɵɵattribute("type", ctx.buttonType);
        i0.ɵɵstyleProp("display", ctx.visible);
        i0.ɵɵclassProp("k-window-titlebar-action", ctx.buttonClass);
      }
    },
    inputs: {
      window: "window"
    },
    exportAs: ["kendoWindowMinimizeAction"],
    features: [i0.ɵɵProvidersFeature([LocalizationService, {
      provide: L10N_PREFIX,
      useValue: 'kendo.button'
    }]), i0.ɵɵInheritDefinitionFeature],
    attrs: _c8,
    ngContentSelectors: _c0,
    decls: 5,
    vars: 3,
    consts: [["innerCssClass", "k-button-icon", "name", "window-minimize", 3, "svgIcon"], [1, "k-button-icon", "k-icon"], [1, "k-button-icon", 3, "ngClass"], [1, "k-button-text"], ["role", "presentation", 1, "k-image", 3, "src"]],
    template: function WindowMinimizeActionDirective_Template(rf, ctx) {
      if (rf & 1) {
        i0.ɵɵprojectionDef();
        i0.ɵɵconditionalCreate(0, WindowMinimizeActionDirective_Conditional_0_Template, 1, 1, "kendo-icon-wrapper", 0);
        i0.ɵɵconditionalCreate(1, WindowMinimizeActionDirective_Conditional_1_Template, 2, 1, "span", 1);
        i0.ɵɵconditionalCreate(2, WindowMinimizeActionDirective_Conditional_2_Template, 1, 1, "span", 2);
        i0.ɵɵelementStart(3, "span", 3);
        i0.ɵɵprojection(4);
        i0.ɵɵelementEnd();
      }
      if (rf & 2) {
        i0.ɵɵconditional(!ctx.imageUrl && !ctx.iconClass ? 0 : -1);
        i0.ɵɵadvance();
        i0.ɵɵconditional(ctx.imageUrl ? 1 : -1);
        i0.ɵɵadvance();
        i0.ɵɵconditional(ctx.iconClass ? 2 : -1);
      }
    },
    dependencies: [IconWrapperComponent, NgClass],
    encapsulation: 2
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(WindowMinimizeActionDirective, [{
    type: Component,
    args: [{
      exportAs: 'kendoWindowMinimizeAction',
      providers: [LocalizationService, {
        provide: L10N_PREFIX,
        useValue: 'kendo.button'
      }],
      selector: 'button[kendoWindowMinimizeAction]',
      template: `
        @if (!imageUrl && !iconClass) {
          <kendo-icon-wrapper
            innerCssClass="k-button-icon"
            name="window-minimize"
            [svgIcon]="windowMinimizeIcon">
          </kendo-icon-wrapper>
        }
        @if (imageUrl) {
          <span class="k-button-icon k-icon">
            <img [src]="imageUrl" class="k-image" role="presentation" />
          </span>
        }
        @if (iconClass) {
          <span class="k-button-icon" [ngClass]="iconClass"></span>
        }
        <span class="k-button-text"><ng-content></ng-content></span>
        `,
      standalone: true,
      imports: [IconWrapperComponent, NgClass]
    }]
  }], () => [{
    type: i0.ElementRef
  }, {
    type: i0.Renderer2
  }, {
    type: DragResizeService,
    decorators: [{
      type: Optional
    }]
  }, {
    type: i1$1.LocalizationService
  }, {
    type: i0.NgZone
  }], {
    window: [{
      type: Input
    }],
    buttonType: [{
      type: HostBinding,
      args: ['attr.type']
    }],
    buttonClass: [{
      type: HostBinding,
      args: ['class.k-window-titlebar-action']
    }],
    onClick: [{
      type: HostListener,
      args: ['click']
    }],
    visible: [{
      type: HostBinding,
      args: ['style.display']
    }]
  });
})();

/**
 * Represents the [Kendo UI Window component for Angular](https://www.telerik.com/kendo-angular-ui/components/dialogs/window).
 *
 * @example
 * ```html
 * <kendo-window title="My Window" [width]="400" [height]="300">
 * </kendo-window>
 * ```
 *
  * @remarks
 * Supported children components are: {@link CustomMessagesComponent}, {@link WindowTitleBarComponent}.
 */
class WindowComponent {
  el;
  renderer;
  service;
  navigation;
  ngZone;
  localization;
  /**
   * Specifies the query selector used to set the initial focus ([see examples](https://www.telerik.com/kendo-angular-ui/components/dialogs/window/initial-focus)).
   *
   * @remarks
   * This property is related to accessibility.
   */
  autoFocusedElement;
  /**
   * Sets the text in the title bar.
   */
  title;
  /**
   * Specifies if the user can drag the component.
   * @default true
   */
  set draggable(value) {
    this.options.draggable = value;
  }
  get draggable() {
    return this.options.draggable;
  }
  /**
   * Specifies if the user can resize the component.
   * @default true
   */
  set resizable(value) {
    this.options.resizable = value;
  }
  get resizable() {
    return this.options.resizable;
  }
  /**
   * @hidden
   */
  set cssClass(classes) {
    this.setServiceClasses(this._cssClass, classes);
    this._cssClass = classes;
  }
  get cssClass() {
    return this._cssClass;
  }
  /**
   * @hidden
   */
  set htmlAttributes(attributes) {
    setHTMLAttributes(attributes, this.renderer, this.el.nativeElement);
    const el = this.el.nativeElement;
    const dir = el.getAttribute('dir');
    const tIndex = el.getAttribute('tabindex');
    if (this.direction !== dir) {
      this.direction = dir;
    }
    if (this.tabIndex !== tIndex) {
      this.tabIndex = tIndex;
    }
    this._htmlAttributes = attributes;
  }
  get htmlAttributes() {
    return this._htmlAttributes;
  }
  /**
   * Specifies if the content stays in the DOM when minimized.
   * @default false
   */
  keepContent = false;
  /**
   * Sets the initial state of the Window.
   */
  set state(value) {
    this.options.state = value;
  }
  get state() {
    return this.options.state;
  }
  /**
   * Sets the minimum width in pixels.
   * @default 120
   */
  set minWidth(value) {
    this.setOption('minWidth', value);
  }
  get minWidth() {
    return this.options.minWidth;
  }
  /**
   * Sets the minimum height in pixels.
   * @default 100
   */
  set minHeight(value) {
    this.setOption('minHeight', value);
  }
  get minHeight() {
    return this.options.minHeight;
  }
  /**
   * Sets the width in pixels.
   */
  set width(value) {
    this.setOption('width', value);
  }
  get width() {
    return this.options.width;
  }
  /**
   * Sets the height in pixels.
   */
  set height(value) {
    this.setOption('height', value);
  }
  get height() {
    return this.options.height;
  }
  /**
   * Sets the initial top offset in pixels.
   */
  set top(value) {
    this.setOption('top', value);
  }
  get top() {
    return this.options.top;
  }
  /**
   * Sets the initial left offset in pixels.
   */
  set left(value) {
    this.setOption('left', value);
  }
  get left() {
    return this.options.left;
  }
  get closeButtonTitle() {
    if (this.messages && this.messages.closeTitle) {
      return this.messages.closeTitle;
    }
    return this.localization.get('closeTitle');
  }
  get restoreButtonTitle() {
    if (this.messages && this.messages.restoreTitle) {
      return this.messages.restoreTitle;
    }
    return this.localization.get('restoreTitle');
  }
  get maximizeButtonTitle() {
    if (this.messages && this.messages.maximizeTitle) {
      return this.messages.maximizeTitle;
    }
    return this.localization.get('maximizeTitle');
  }
  get minimizeButtonTitle() {
    if (this.messages && this.messages.minimizeTitle) {
      return this.messages.minimizeTitle;
    }
    return this.localization.get('minimizeTitle');
  }
  /**
   * Fires when the user starts to move the Window.
   */
  dragStart = new EventEmitter();
  /**
   * Fires after the Window has been moved by the user.
   */
  dragEnd = new EventEmitter();
  /**
   * Fires when the user starts to resize the Window.
   */
  resizeStart = new EventEmitter();
  /**
   * Fires after the Window has been resized by the user.
   */
  resizeEnd = new EventEmitter();
  /**
   * Fires when the user closes the Window.
   */
  close = new EventEmitter();
  /**
   * Fires when the `width` property is updated after resizing. The event data contains the new width. Allows two-way binding of the `width` property.
   */
  widthChange = new EventEmitter();
  /**
   * Fires when the `height` property is updated after resizing. The event data contains the new height. Allows two-way binding of the `height` property.
   */
  heightChange = new EventEmitter();
  /**
   * Fires when the `top` property is updated after dragging or resizing. The event data contains the new top offset. Allows two-way binding of the `top` property.
   */
  topChange = new EventEmitter();
  /**
   * Fires when the `left` property is updated after dragging or resizing. The event data contains the new left offset. Allows two-way binding of the `left` property.
   */
  leftChange = new EventEmitter();
  /**
   * Fires when the `state` property is updated. The event data contains the new state. Allows two-way binding of the `state` property.
   */
  stateChange = new EventEmitter();
  /**
   * @hidden
   */
  contentTemplate;
  /**
   * @hidden
   */
  titleBarTemplate;
  /**
   * @hidden
   */
  messages = {};
  /**
   * @hidden
   */
  showLicenseWatermark = false;
  /**
   * @hidden
   */
  licenseMessage;
  tabIndex = 0;
  role = 'dialog';
  hostClass = true;
  get dir() {
    return this.direction;
  }
  titleBarView;
  titleBarContent;
  resizeHandles;
  resizeDirections;
  /**
   * @hidden
   */
  titleId = null;
  _htmlAttributes;
  _cssClass;
  direction;
  draged = false;
  resized = false;
  windowSubscription = new Subscription();
  domSubs = new Subscription();
  localizationChangeSubscription;
  stateAppliedByAction = null;
  constructor(el, renderer, service, navigation, ngZone, localization) {
    this.el = el;
    this.renderer = renderer;
    this.service = service;
    this.navigation = navigation;
    this.ngZone = ngZone;
    this.localization = localization;
    const isValid = validatePackage(packageMetadata);
    this.licenseMessage = getLicenseMessage(packageMetadata);
    this.showLicenseWatermark = shouldShowValidationUI(isValid);
    this.direction = this.localization.rtl ? 'rtl' : 'ltr';
    this.localizationChangeSubscription = this.localization.changes.subscribe(({
      rtl
    }) => this.direction = rtl ? 'rtl' : 'ltr');
    this.resizeDirections = RESIZE_DIRECTIONS;
    this.subscribeEvents();
    this.titleId = this.generateTitleId();
  }
  ngAfterViewInit() {
    if (!isDocumentAvailable()) {
      return;
    }
    this.setNextZIndex();
    this.ngZone.onStable.pipe(take(1)).subscribe(() => {
      this.handleInitialFocus();
    });
    this.ngZone.runOutsideAngular(() => Promise.resolve(null).then(() => this.setInitialOffset()));
    this.initDomEvents();
    if (this.titleBarView && !this.titleBarView.template) {
      this.renderer.setAttribute(this.el.nativeElement, 'aria-labelledby', this.titleId);
    }
  }
  ngOnInit() {
    this.renderer.removeAttribute(this.el.nativeElement, 'title');
    this.service.init(this.el);
  }
  ngOnChanges(changes) {
    OFFSET_STYLES.forEach(style => {
      if (isChanged(style, changes)) {
        this.setStyle(style, this.options[style]);
      }
    });
    if (isChanged('draggable', changes)) {
      const titleBar = isPresent(this.titleBarContent) ? this.titleBarContent : this.titleBarView;
      if (isTruthy(changes['draggable'].currentValue)) {
        titleBar.subscribeDrag();
      } else {
        titleBar.unsubscribeDrag();
      }
    }
    if (isChanged('state', changes)) {
      const appliedByAction = this.stateAppliedByAction === changes['state'].currentValue;
      this.stateAppliedByAction = null;
      if (!appliedByAction) {
        this.service.applyManualState(changes['state'].previousValue);
        this.updateAllOffset();
      }
    }
  }
  ngOnDestroy() {
    if (this.windowSubscription) {
      this.windowSubscription.unsubscribe();
    }
    if (this.domSubs) {
      this.domSubs.unsubscribe();
    }
    this.localizationChangeSubscription.unsubscribe();
  }
  /**
   * Focuses the wrapper of the Window component.
   */
  focus() {
    const wrapper = this.el.nativeElement;
    if (isPresent(wrapper)) {
      wrapper.focus({
        preventScroll: true
      });
    }
  }
  /**
   * Brings the current Window component on top of other Window components on the page.
   */
  bringToFront() {
    this.setNextZIndex();
  }
  /**
   * Manually updates the `width` or `height` option of the Window.
   * The required style will be applied to the Window wrapper element and the
   * corresponding property of the component instance will be updated.
   * This method is intended to be used for sizing dynamically created components using the
   * [`WindowService`](https://www.telerik.com/kendo-angular-ui/components/dialogs/api/windowservice)
   * @param dimension The option to update.
   * @param value The value in pixels.
   */
  setDimension(dimension, value) {
    this.setOption(dimension, value);
    this.setStyle(dimension, value);
  }
  /**
   * Manually updates the `top` or `left` offset of the Window.
   * The required style will be applied to the Window wrapper element and the
   * corresponding property of the component instance will be updated.
   * This method is intended to be used for positioning dynamically created components using the
   * [`WindowService`](https://www.telerik.com/kendo-angular-ui/components/dialogs/api/windowservice)
   * @param offset The option to update.
   * @param value The value in pixels.
   */
  setOffset(offset, value) {
    this.setOption(offset, value);
    this.setStyle(offset, value);
  }
  get showDefaultTitleBar() {
    return !isPresent(this.titleBarContent);
  }
  get styleMinWidth() {
    return this.minWidth + 'px';
  }
  get styleMinHeight() {
    if (this.state === 'minimized') {
      return null;
    }
    return isPresent(this.minHeight) ? this.minHeight + 'px' : null;
  }
  get stylePosition() {
    return this.options.position;
  }
  get wrapperMaximizedClass() {
    return this.state === 'maximized';
  }
  get wrapperMinimizedClass() {
    return this.state === 'minimized';
  }
  /**
   * @hidden
   */
  onComponentFocus() {
    this.renderer.addClass(this.el.nativeElement, 'k-focus');
    this.setNextZIndex();
  }
  /**
   * @hidden
   */
  onComponentBlur() {
    this.renderer.removeClass(this.el.nativeElement, 'k-focus');
  }
  subscribeEvents() {
    if (!isDocumentAvailable()) {
      return;
    }
    this.windowSubscription.add(this.service.focus.subscribe(() => {
      this.el.nativeElement.focus({
        preventScroll: true
      });
    }));
    this.windowSubscription.add(this.service.dragStart.subscribe(() => {
      this.draged = true;
      this.ngZone.run(() => {
        this.dragStart.emit();
      });
    }));
    this.windowSubscription.add(this.service.dragEnd.subscribe(() => {
      if (this.draged) {
        this.draged = false;
        this.ngZone.run(() => {
          this.dragEnd.emit();
        });
      }
    }));
    this.windowSubscription.add(this.service.close.subscribe(() => {
      this.close.emit();
    }));
    this.windowSubscription.add(this.service.resizeStart.subscribe(() => {
      this.resized = true;
      this.ngZone.run(() => {
        this.resizeStart.emit();
      });
    }));
    this.windowSubscription.add(this.service.resizeEnd.subscribe(() => {
      if (this.resized) {
        this.resized = false;
        this.ngZone.run(() => {
          this.resizeEnd.emit();
        });
      }
    }));
    this.windowSubscription.add(this.service.change.subscribe(ev => {
      OFFSET_STYLES.forEach(style => {
        if (isPresent(ev[style])) {
          this.setStyle(style, ev[style]);
          if (this.state !== 'maximized') {
            const emitter = this[style + 'Change'];
            if (emitter.observers.length) {
              this.ngZone.run(() => {
                emitter.emit(ev[style]);
              });
            }
          }
        }
      });
    }));
    this.windowSubscription.add(this.service.stateChange.subscribe(state => {
      if (isPresent(this.service.lastAction)) {
        this.updateAllOffset();
        this.service.lastAction = null;
        this.stateAppliedByAction = state;
        this.stateChange.emit(state);
      }
    }));
  }
  initDomEvents() {
    if (!this.el) {
      return;
    }
    this.ngZone.runOutsideAngular(() => {
      this.domSubs.add(this.renderer.listen(this.el.nativeElement, 'keydown', ev => {
        this.onKeyDown(ev);
      }));
    });
  }
  onKeyDown(event) {
    this.navigation.process(event);
  }
  setServiceClasses(prevValue, value) {
    const el = this.el.nativeElement;
    if (prevValue) {
      parseCSSClassNames(prevValue).forEach(className => {
        this.renderer.removeClass(el, className);
      });
    }
    if (value) {
      parseCSSClassNames(value).forEach(className => {
        this.renderer.addClass(el, className);
      });
    }
  }
  setNextZIndex() {
    const currentZIndex = this.el.nativeElement.style['z-index'];
    const nextPossibleZIndex = this.service.nextPossibleZIndex;
    if (!currentZIndex || nextPossibleZIndex - currentZIndex > 1) {
      this.renderer.setStyle(this.el.nativeElement, "z-index", this.service.nextZIndex);
    }
  }
  setInitialOffset() {
    if (this.state !== 'maximized') {
      this.updateAllOffset();
      if (!isPresent(this.left) || !isPresent(this.top)) {
        this.service.center();
      }
    } else {
      const viewPort = this.service.windowViewPort;
      this.setStyle('width', viewPort.width);
      this.setStyle('height', viewPort.height);
      this.setStyle('top', 0);
      this.setStyle('left', 0);
    }
  }
  updateAllOffset() {
    OFFSET_STYLES.forEach(style => {
      if (isPresent(this[style])) {
        this.setStyle(style, this[style]);
      } else {
        this.removeStyle(style);
      }
    });
  }
  setStyle(style, value) {
    this.renderer.setStyle(this.el.nativeElement, style, value + 'px');
  }
  removeStyle(style) {
    this.renderer.removeStyle(this.el.nativeElement, style);
  }
  get options() {
    return this.service.options;
  }
  setOption(style, value) {
    if (typeof value !== 'number' && typeof value !== 'string') {
      return;
    }
    const parsedValue = typeof value === 'number' ? value : parseInt(value, 10);
    this.options[style] = parsedValue;
    this.service.setRestoreOption(style, parsedValue);
  }
  handleInitialFocus() {
    const wrapper = this.el.nativeElement;
    if (this.autoFocusedElement) {
      const initiallyFocusedElement = wrapper.querySelector(this.autoFocusedElement);
      if (initiallyFocusedElement) {
        initiallyFocusedElement.focus({
          preventScroll: true
        });
      }
    } else {
      this.focus();
    }
  }
  /**
   * @hidden
   */
  generateTitleId() {
    return 'kendo-window-title-' + Math.ceil(Math.random() * 1000000).toString();
  }
  static ɵfac = function WindowComponent_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || WindowComponent)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.Renderer2), i0.ɵɵdirectiveInject(DragResizeService), i0.ɵɵdirectiveInject(NavigationService), i0.ɵɵdirectiveInject(i0.NgZone), i0.ɵɵdirectiveInject(i1$1.LocalizationService));
  };
  static ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
    type: WindowComponent,
    selectors: [["kendo-window"]],
    contentQueries: function WindowComponent_ContentQueries(rf, ctx, dirIndex) {
      if (rf & 1) {
        i0.ɵɵcontentQuery(dirIndex, WindowTitleBarComponent, 5);
      }
      if (rf & 2) {
        let _t;
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.titleBarContent = _t.first);
      }
    },
    viewQuery: function WindowComponent_Query(rf, ctx) {
      if (rf & 1) {
        i0.ɵɵviewQuery(WindowTitleBarComponent, 5)(ResizeHandleDirective, 5);
      }
      if (rf & 2) {
        let _t;
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.titleBarView = _t.first);
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.resizeHandles = _t);
      }
    },
    hostVars: 15,
    hostBindings: function WindowComponent_HostBindings(rf, ctx) {
      if (rf & 1) {
        i0.ɵɵlistener("focus", function WindowComponent_focus_HostBindingHandler() {
          return ctx.onComponentFocus();
        })("blur", function WindowComponent_blur_HostBindingHandler() {
          return ctx.onComponentBlur();
        });
      }
      if (rf & 2) {
        i0.ɵɵattribute("tabIndex", ctx.tabIndex)("role", ctx.role)("dir", ctx.dir);
        i0.ɵɵstyleProp("min-width", ctx.styleMinWidth)("min-height", ctx.styleMinHeight)("position", ctx.stylePosition);
        i0.ɵɵclassProp("k-window", ctx.hostClass)("k-window-maximized", ctx.wrapperMaximizedClass)("k-window-minimized", ctx.wrapperMinimizedClass);
      }
    },
    inputs: {
      autoFocusedElement: "autoFocusedElement",
      title: "title",
      draggable: "draggable",
      resizable: "resizable",
      keepContent: "keepContent",
      state: "state",
      minWidth: "minWidth",
      minHeight: "minHeight",
      width: "width",
      height: "height",
      top: "top",
      left: "left"
    },
    outputs: {
      dragStart: "dragStart",
      dragEnd: "dragEnd",
      resizeStart: "resizeStart",
      resizeEnd: "resizeEnd",
      close: "close",
      widthChange: "widthChange",
      heightChange: "heightChange",
      topChange: "topChange",
      leftChange: "leftChange",
      stateChange: "stateChange"
    },
    exportAs: ["kendoWindow"],
    features: [i0.ɵɵProvidersFeature([DragResizeService, NavigationService, LocalizationService, {
      provide: L10N_PREFIX,
      useValue: 'kendo.window'
    }, {
      provide: KENDO_WEBMCP_HOST,
      useExisting: forwardRef(() => WindowComponent)
    }]), i0.ɵɵNgOnChangesFeature],
    ngContentSelectors: _c10,
    decls: 7,
    vars: 5,
    consts: () => {
      let i18n_2;
      if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
        /**
         * @desc The title of the close button
         * @meaning kendo.window.closeTitle
         */
        const MSG_D__PROJECTS_FOREVER_MEMORIES_PROJ_FOREVER_MEMORIES_FOREVER_WEB_NODE_MODULES__PROGRESS_KENDO_ANGULAR_DIALOG_FESM2022_PROGRESS_KENDO_ANGULAR_DIALOG_MJS_2 = goog.getMsg("Close");
        i18n_2 = MSG_D__PROJECTS_FOREVER_MEMORIES_PROJ_FOREVER_MEMORIES_FOREVER_WEB_NODE_MODULES__PROGRESS_KENDO_ANGULAR_DIALOG_FESM2022_PROGRESS_KENDO_ANGULAR_DIALOG_MJS_2;
      } else {
        i18n_2 = $localize`:kendo.window.closeTitle|The title of the close button:Close`;
      }
      let i18n_3;
      if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
        /**
         * @desc The title of the restore button
         * @meaning kendo.window.restoreTitle
         */
        const MSG_D__PROJECTS_FOREVER_MEMORIES_PROJ_FOREVER_MEMORIES_FOREVER_WEB_NODE_MODULES__PROGRESS_KENDO_ANGULAR_DIALOG_FESM2022_PROGRESS_KENDO_ANGULAR_DIALOG_MJS_3 = goog.getMsg("Restore");
        i18n_3 = MSG_D__PROJECTS_FOREVER_MEMORIES_PROJ_FOREVER_MEMORIES_FOREVER_WEB_NODE_MODULES__PROGRESS_KENDO_ANGULAR_DIALOG_FESM2022_PROGRESS_KENDO_ANGULAR_DIALOG_MJS_3;
      } else {
        i18n_3 = $localize`:kendo.window.restoreTitle|The title of the restore button:Restore`;
      }
      let i18n_4;
      if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
        /**
         * @desc The title of the maximize button
         * @meaning kendo.window.maximizeTitle
         */
        const MSG_D__PROJECTS_FOREVER_MEMORIES_PROJ_FOREVER_MEMORIES_FOREVER_WEB_NODE_MODULES__PROGRESS_KENDO_ANGULAR_DIALOG_FESM2022_PROGRESS_KENDO_ANGULAR_DIALOG_MJS_4 = goog.getMsg("Maximize");
        i18n_4 = MSG_D__PROJECTS_FOREVER_MEMORIES_PROJ_FOREVER_MEMORIES_FOREVER_WEB_NODE_MODULES__PROGRESS_KENDO_ANGULAR_DIALOG_FESM2022_PROGRESS_KENDO_ANGULAR_DIALOG_MJS_4;
      } else {
        i18n_4 = $localize`:kendo.window.maximizeTitle|The title of the maximize button:Maximize`;
      }
      let i18n_5;
      if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
        /**
         * @desc The title of the minimize button
         * @meaning kendo.window.minimizeTitle
         */
        const MSG_D__PROJECTS_FOREVER_MEMORIES_PROJ_FOREVER_MEMORIES_FOREVER_WEB_NODE_MODULES__PROGRESS_KENDO_ANGULAR_DIALOG_FESM2022_PROGRESS_KENDO_ANGULAR_DIALOG_MJS_5 = goog.getMsg("Minimize");
        i18n_5 = MSG_D__PROJECTS_FOREVER_MEMORIES_PROJ_FOREVER_MEMORIES_FOREVER_WEB_NODE_MODULES__PROGRESS_KENDO_ANGULAR_DIALOG_FESM2022_PROGRESS_KENDO_ANGULAR_DIALOG_MJS_5;
      } else {
        i18n_5 = $localize`:kendo.window.minimizeTitle|The title of the minimize button:Minimize`;
      }
      return [["kendoWindowLocalizedMessages", "", "closeTitle", i18n_2, "restoreTitle", i18n_3, "maximizeTitle", i18n_4, "minimizeTitle", i18n_5], [3, "template", "id"], [1, "k-window-content", 3, "hidden"], ["kendoWatermarkOverlay", "", 3, "licenseMessage"], [1, "k-window-title"], [1, "k-window-titlebar-actions"], ["kendoWindowMinimizeAction", ""], ["kendoWindowMaximizeAction", ""], ["kendoWindowRestoreAction", ""], ["kendoWindowCloseAction", ""], [3, "ngTemplateOutlet"], ["kendoWindowResizeHandle", "", "kendoDraggable", "", 3, "direction"]];
    },
    template: function WindowComponent_Template(rf, ctx) {
      if (rf & 1) {
        i0.ɵɵprojectionDef(_c9);
        i0.ɵɵelementContainerStart(0, 0)(1);
        i0.ɵɵconditionalCreate(2, WindowComponent_Conditional_2_Template, 8, 11, "kendo-window-titlebar", 1);
        i0.ɵɵconditionalCreate(3, WindowComponent_Conditional_3_Template, 1, 0);
        i0.ɵɵconditionalCreate(4, WindowComponent_Conditional_4_Template, 3, 3, "div", 2);
        i0.ɵɵconditionalCreate(5, WindowComponent_Conditional_5_Template, 2, 0);
        i0.ɵɵconditionalCreate(6, WindowComponent_Conditional_6_Template, 1, 1, "div", 3);
        i0.ɵɵelementContainerEnd()();
      }
      if (rf & 2) {
        i0.ɵɵadvance(2);
        i0.ɵɵconditional(ctx.showDefaultTitleBar ? 2 : -1);
        i0.ɵɵadvance();
        i0.ɵɵconditional(!ctx.showDefaultTitleBar ? 3 : -1);
        i0.ɵɵadvance();
        i0.ɵɵconditional(ctx.state !== "minimized" || ctx.keepContent ? 4 : -1);
        i0.ɵɵadvance();
        i0.ɵɵconditional(ctx.resizable ? 5 : -1);
        i0.ɵɵadvance();
        i0.ɵɵconditional(ctx.showLicenseWatermark ? 6 : -1);
      }
    },
    dependencies: [LocalizedMessagesDirective, WindowTitleBarComponent, WindowMinimizeActionDirective, WindowMaximizeActionDirective, WindowRestoreActionDirective, WindowCloseActionDirective, NgTemplateOutlet, ResizeHandleDirective, DraggableDirective, WatermarkOverlayComponent],
    encapsulation: 2
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(WindowComponent, [{
    type: Component,
    args: [{
      exportAs: 'kendoWindow',
      providers: [DragResizeService, NavigationService, LocalizationService, {
        provide: L10N_PREFIX,
        useValue: 'kendo.window'
      }, {
        provide: KENDO_WEBMCP_HOST,
        useExisting: forwardRef(() => WindowComponent)
      }],
      selector: 'kendo-window',
      template: `
        <ng-container kendoWindowLocalizedMessages
          i18n-closeTitle="kendo.window.closeTitle|The title of the close button"
          closeTitle="Close"

          i18n-restoreTitle="kendo.window.restoreTitle|The title of the restore button"
          restoreTitle="Restore"

          i18n-maximizeTitle="kendo.window.maximizeTitle|The title of the maximize button"
          maximizeTitle="Maximize"

          i18n-minimizeTitle="kendo.window.minimizeTitle|The title of the minimize button"
          minimizeTitle="Minimize"
          >
          <ng-container>

            @if (showDefaultTitleBar) {
              <kendo-window-titlebar [template]="titleBarTemplate" [id]="titleId">
                <span class="k-window-title">{{ title }}</span>
                <div class="k-window-titlebar-actions">
                  <button kendoWindowMinimizeAction  [attr.title]="minimizeButtonTitle" [attr.aria-label]="minimizeButtonTitle"></button>
                  <button kendoWindowMaximizeAction [attr.title]="maximizeButtonTitle" [attr.aria-label]="maximizeButtonTitle"></button>
                  <button kendoWindowRestoreAction [attr.title]="restoreButtonTitle" [attr.aria-label]="restoreButtonTitle"></button>
                  <button kendoWindowCloseAction [attr.title]="closeButtonTitle" [attr.aria-label]="closeButtonTitle"></button>
                </div>
              </kendo-window-titlebar>
            }
            @if (!showDefaultTitleBar) {
              <ng-content select="kendo-window-titlebar"></ng-content>
            }

            @if (state !== 'minimized' || keepContent) {
              <div
                [hidden]="state === 'minimized' && keepContent"
                class="k-window-content"
                >
                @if (!contentTemplate) {
                  <ng-content></ng-content>
                }
                @if (contentTemplate) {
                  <ng-template [ngTemplateOutlet]="contentTemplate"></ng-template>
                }
              </div>
            }

            @if (resizable) {
              @for (dir of resizeDirections; track dir) {
                <div
                  [direction]="dir"
                  kendoWindowResizeHandle
                  kendoDraggable>
                </div>
              }
            }

            @if (showLicenseWatermark) {
              <div kendoWatermarkOverlay [licenseMessage]="licenseMessage"></div>
            }
        `,
      standalone: true,
      imports: [LocalizedMessagesDirective, WindowTitleBarComponent, WindowMinimizeActionDirective, WindowMaximizeActionDirective, WindowRestoreActionDirective, WindowCloseActionDirective, NgTemplateOutlet, ResizeHandleDirective, DraggableDirective, WatermarkOverlayComponent]
    }]
  }], () => [{
    type: i0.ElementRef
  }, {
    type: i0.Renderer2
  }, {
    type: DragResizeService
  }, {
    type: NavigationService
  }, {
    type: i0.NgZone
  }, {
    type: i1$1.LocalizationService
  }], {
    autoFocusedElement: [{
      type: Input
    }],
    title: [{
      type: Input
    }],
    draggable: [{
      type: Input
    }],
    resizable: [{
      type: Input
    }],
    keepContent: [{
      type: Input
    }],
    state: [{
      type: Input
    }],
    minWidth: [{
      type: Input
    }],
    minHeight: [{
      type: Input
    }],
    width: [{
      type: Input
    }],
    height: [{
      type: Input
    }],
    top: [{
      type: Input
    }],
    left: [{
      type: Input
    }],
    dragStart: [{
      type: Output
    }],
    dragEnd: [{
      type: Output
    }],
    resizeStart: [{
      type: Output
    }],
    resizeEnd: [{
      type: Output
    }],
    close: [{
      type: Output
    }],
    widthChange: [{
      type: Output
    }],
    heightChange: [{
      type: Output
    }],
    topChange: [{
      type: Output
    }],
    leftChange: [{
      type: Output
    }],
    stateChange: [{
      type: Output
    }],
    tabIndex: [{
      type: HostBinding,
      args: ['attr.tabIndex']
    }],
    role: [{
      type: HostBinding,
      args: ['attr.role']
    }],
    hostClass: [{
      type: HostBinding,
      args: ['class.k-window']
    }],
    dir: [{
      type: HostBinding,
      args: ['attr.dir']
    }],
    titleBarView: [{
      type: ViewChild,
      args: [WindowTitleBarComponent, {
        static: false
      }]
    }],
    titleBarContent: [{
      type: ContentChild,
      args: [WindowTitleBarComponent, {
        static: false
      }]
    }],
    resizeHandles: [{
      type: ViewChildren,
      args: [ResizeHandleDirective]
    }],
    styleMinWidth: [{
      type: HostBinding,
      args: ['style.minWidth']
    }],
    styleMinHeight: [{
      type: HostBinding,
      args: ['style.minHeight']
    }],
    stylePosition: [{
      type: HostBinding,
      args: ['style.position']
    }],
    wrapperMaximizedClass: [{
      type: HostBinding,
      args: ['class.k-window-maximized']
    }],
    wrapperMinimizedClass: [{
      type: HostBinding,
      args: ['class.k-window-minimized']
    }],
    onComponentFocus: [{
      type: HostListener,
      args: ['focus']
    }],
    onComponentBlur: [{
      type: HostListener,
      args: ['blur']
    }]
  });
})();

/**
 * Indicates that the **Close** button of a Window opened through `WindowService` is clicked.
 *
 */
class WindowCloseResult {}

/**
 * Holds references to the Window instance. Controls Windows opened through `WindowService`. ([See example.](https://www.telerik.com/kendo-angular-ui/components/dialogs/api/windowservice#open))
 */
class WindowRef {
  /**
   * Represents a reference to the Window instance.
   */
  window;
  /**
   * Represents a reference to the child component of the Window. Available when you open the Window with [component content](https://www.telerik.com/kendo-angular-ui/components/dialogs/window/service#rendering-the-content-area).
   */
  content;
  /**
   * Allows you to close the Window by using code. When called with no arguments, the `result` Observable is of type `WindowCloseResult`. When called with an argument, the `result` Observable holds the provided value.
   */
  close;
  /**
   * Emits events when the Window is closed through the `Esc` key, the **Close** button of the title bar, or by calling the `close` method. When the Window is closed with the title bar button, `Esc`, or by calling `close` with no arguments, the result is of type [`WindowCloseResult`](https://www.telerik.com/kendo-angular-ui/components/dialogs/api/windowcloseresult). When `close` is called with an argument, the result is the passed argument.
   */
  result;
}

/**
 * Represents the settings for the Window actions when you open a Window through `WindowService`. ([See example.](https://www.telerik.com/kendo-angular-ui/components/dialogs/api/windowservice#open))
 */
class WindowSettings {
  /**
   * Use the `preventClose` predicate to check if closing the Window should be prevented. Applies to clicking the **Close** button, pressing **Esc**, or calling the `close` method. Return `true` to prevent closing. If the **Close** button or **Esc** is used, a [`WindowCloseResult`](https://www.telerik.com/kendo-angular-ui/components/dialogs/api/windowcloseresult) instance is passed.
   * @param {any} ev The event argument.
   * @param {WindowRef} [windowRef] - The window reference, provided only when you create the window using a component.
   * @returns Returns `true` to prevent closing the window.
   */
  preventClose;
  /**
   * Sets the Window `title`.
   */
  title;
  /**
   * Defines the Window `content`.
   */
  content;
  /**
   * Defines the content of the title bar.
   */
  titleBarContent;
  /**
   * Sets the text of the labels shown in the Window. Use for localization.
   */
  messages;
  /**
   * Set to `true` to persist the Window content in the DOM when minimized.
   */
  keepContent;
  /**
   * Sets the width of the Window in pixels.
   */
  width;
  /**
   * Sets the minimum width of the Window in pixels.
   */
  minWidth;
  /**
   * Sets the height of the Window in pixels.
   */
  height;
  /**
   * Sets the minimum height of the Window in pixels.
   */
  minHeight;
  /**
   * Sets the left offset of the Window in pixels.
   */
  left;
  /**
   * Sets the top offset of the Window in pixels.
   */
  top;
  /**
   * Specifies is the Window is draggable.
   */
  draggable;
  /**
   * Sets custom CSS classes for the Window wrapper element. Accepts any value supported by [`ngClass`](link:site.data.urls.angular['ngclassapi']).
   */
  cssClass;
  /**
   * Sets HTML attributes for the Window wrapper element. Accepts string key-value pairs.
   *
   * @remarks
   * This option is related to accessibility.
   */
  htmlAttributes;
  /**
   * Specifies if the Window is resizable.
   */
  resizable;
  /**
   * Sets the initial state of the Window.
   */
  state;
  /**
   * Defines the container where the Window is inserted. This changes the place in the page hierarchy where the Window appears.
   */
  appendTo;
  /**
   * Sets the query selector for the element to focus automatically.
   */
  autoFocusedElement;
}

/**
 * @hidden
 */
class WindowContainerService {
  static container = null;
  set container(container) {
    WindowContainerService.container = container;
  }
  get container() {
    return WindowContainerService.container;
  }
  static ɵfac = function WindowContainerService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || WindowContainerService)();
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: WindowContainerService,
    factory: WindowContainerService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(WindowContainerService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], null, null);
})();
const isNotComponent = component => isString(component) || component instanceof TemplateRef;
class WindowInjector {
  getWindowRef;
  parentInjector;
  constructor(getWindowRef, parentInjector) {
    this.getWindowRef = getWindowRef;
    this.parentInjector = parentInjector;
  }
  get(token, notFoundValue) {
    if (token === WindowRef) {
      return this.getWindowRef();
    }
    return this.parentInjector.get(token, notFoundValue);
  }
}
/**
 * Provides methods for opening Windows dynamically.
 *
 * Use this service to open a Window component and manage its lifecycle. ([See example.](https://www.telerik.com/kendo-angular-ui/components/dialogs/window/service))
 */
class WindowService {
  containerService;
  constructor(containerService) {
    this.containerService = containerService;
  }
  /**
   * Opens a Window component with the specified settings.
   *
   * @param settings The settings that configure the Window.
   * @returns A `WindowRef` instance for controlling the Window.
   *
   * @example
   * ```ts
   * const window = windowService.open({
   *   title: 'My window',
   *   content: 'My content!'
   * });
   * ```
   */
  open(settings) {
    const container = settings.appendTo || this.containerService.container;
    if (!container) {
      throw new Error(`Cannot attach window to the page.
                Add an element that uses the kendoWindowContainer directive, or set the 'appendTo' property.
                See https://www.telerik.com/kendo-angular-ui/components/dialogs/window/service/
            `);
    }
    const windowRef = {
      close: () => {},
      content: null,
      result: null,
      window: null
    };
    const content = this.contentFrom(settings.content, container, windowRef);
    const window = container.createComponent(WindowComponent, {
      projectableNodes: content.nodes
    });
    windowRef.window = window;
    this.applyOptions(window.instance, settings);
    const apiClose = new Subject();
    const close = e => {
      apiClose.next(e || new WindowCloseResult());
      if (content.componentRef) {
        content.componentRef.destroy();
      }
      window.destroy();
    };
    const result = merge(apiClose, window.instance.close.pipe(map(e => e ? e : new WindowCloseResult()), filter(e => {
      if (settings.preventClose) {
        // add windowRef only when using component
        const windowRefParameter = isNotComponent(settings.content) ? undefined : windowRef;
        return !settings.preventClose(e, windowRefParameter);
      }
      return true;
    }))).pipe(take(1),
    // Takes care for multiple subscriptions:
    // We subscribe internally and the user may subscribe to get a close result - window.result.subscribe().
    // This causes multiple subscriptions to the same source and thus multiple emissions. share() solves that.
    share());
    result.subscribe(close);
    windowRef.close = close;
    windowRef.result = result;
    window.changeDetectorRef.markForCheck();
    return windowRef;
  }
  applyOptions(instance, options) {
    if (isPresent(options.htmlAttributes)) {
      instance.htmlAttributes = options.htmlAttributes;
    }
    if (isPresent(options.cssClass)) {
      instance.cssClass = options.cssClass;
    }
    if (isPresent(options.title)) {
      instance.title = options.title;
    }
    if (isPresent(options.keepContent)) {
      instance.keepContent = options.keepContent;
    }
    if (isPresent(options.width)) {
      instance.width = options.width;
    }
    if (isPresent(options.minWidth)) {
      instance.minWidth = options.minWidth;
    }
    if (isPresent(options.height)) {
      instance.height = options.height;
    }
    if (isPresent(options.minHeight)) {
      instance.minHeight = options.minHeight;
    }
    if (isPresent(options.left)) {
      instance.left = options.left;
    }
    if (isPresent(options.top)) {
      instance.top = options.top;
    }
    if (isPresent(options.draggable)) {
      instance.draggable = options.draggable;
    }
    if (isPresent(options.resizable)) {
      instance.resizable = options.resizable;
    }
    if (isPresent(options.messages && options.messages.closeTitle)) {
      instance.messages.closeTitle = options.messages.closeTitle;
    }
    if (isPresent(options.messages && options.messages.restoreTitle)) {
      instance.messages.restoreTitle = options.messages.restoreTitle;
    }
    if (isPresent(options.messages && options.messages.maximizeTitle)) {
      instance.messages.maximizeTitle = options.messages.maximizeTitle;
    }
    if (isPresent(options.messages && options.messages.minimizeTitle)) {
      instance.messages.minimizeTitle = options.messages.minimizeTitle;
    }
    if (isPresent(options.autoFocusedElement)) {
      instance.autoFocusedElement = options.autoFocusedElement;
    }
    if (isPresent(options.state)) {
      instance.state = options.state;
      if (options.state === 'minimized') {
        instance.keepContent = true;
      }
    }
    if (options.content instanceof TemplateRef) {
      instance.contentTemplate = options.content;
    }
    if (options.titleBarContent instanceof TemplateRef) {
      instance.titleBarTemplate = options.titleBarContent;
    }
  }
  contentFrom(content, container, windowRef) {
    const renderer = container.injector.get(Renderer2);
    let nodes = [];
    let componentRef = null;
    if (typeof content === 'string') {
      nodes = [renderer.createText(content)];
    } else if (content && !(content instanceof TemplateRef)) {
      const injector = new WindowInjector(() => windowRef, container.injector);
      componentRef = container.createComponent(content, {
        injector
      });
      nodes = [componentRef.location.nativeElement];
      windowRef.content = componentRef;
    }
    return {
      componentRef,
      nodes: [[],
      // TitleBar
      nodes // Content
      ]
    };
  }
  static ɵfac = function WindowService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || WindowService)(i0.ɵɵinject(WindowContainerService));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: WindowService,
    factory: WindowService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(WindowService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: WindowContainerService,
    decorators: [{
      type: Inject,
      args: [WindowContainerService]
    }]
  }], null);
})();

/**
 * Represents an insertion point for Dialogs created through the Dialog service.
 * The created Dialogs mount after this element. ([See example.](https://www.telerik.com/kendo-angular-ui/components/dialogs/api/dialogservice#open))
 *
 * @example
 * ```html
 * <div kendoDialogContainer></div>
 * ```
 */
class DialogContainerDirective {
  constructor(container, service) {
    service.container = container;
  }
  static ɵfac = function DialogContainerDirective_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DialogContainerDirective)(i0.ɵɵdirectiveInject(i0.ViewContainerRef), i0.ɵɵdirectiveInject(DialogContainerService));
  };
  static ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
    type: DialogContainerDirective,
    selectors: [["", "kendoDialogContainer", ""]]
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DialogContainerDirective, [{
    type: Directive,
    args: [{
      selector: '[kendoDialogContainer]',
      standalone: true
    }]
  }], () => [{
    type: i0.ViewContainerRef
  }, {
    type: DialogContainerService
  }], null);
})();

/**
 * Represents a component for customizing the messages in Dialog and Window components.
 *
 * Use the `CustomMessagesComponent` to override default messages with custom text. ([See example.](https://www.telerik.com/kendo-angular-ui/components/dialogs/globalization#internationalization)).
 *
 * @example
 * ```typescript
 * import { CustomMessagesComponent } from './custom-messages.component';
 *
 * @Component({
 *   selector: 'my-dialog',
 *   template: `
 *     <kendo-dialog>
 *       <kendo-dialog-messages
 *         [closeTitle]="'Close dialog'"
 *         [restoreTitle]="'Restore dialog'"
 *         [maximizeTitle]="'Maximize dialog'"
 *         [minimizeTitle]="'Minimize dialog'">
 *       </kendo-dialog-messages>
 *     </kendo-dialog>
 *   `
 * })
 * export class MyDialogComponent {}
 * ```
 */
class CustomMessagesComponent extends Messages {
  service;
  constructor(service) {
    super();
    this.service = service;
  }
  get override() {
    return true;
  }
  static ɵfac = function CustomMessagesComponent_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || CustomMessagesComponent)(i0.ɵɵdirectiveInject(i1$1.LocalizationService));
  };
  static ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
    type: CustomMessagesComponent,
    selectors: [["kendo-dialog-messages"], ["kendo-window-messages"]],
    features: [i0.ɵɵProvidersFeature([{
      provide: Messages,
      useExisting: forwardRef(() => CustomMessagesComponent)
    }]), i0.ɵɵInheritDefinitionFeature]
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(CustomMessagesComponent, [{
    type: Directive,
    args: [{
      providers: [{
        provide: Messages,
        useExisting: forwardRef(() => CustomMessagesComponent)
      }],
      selector: 'kendo-dialog-messages, kendo-window-messages',
      standalone: true
    }]
  }], () => [{
    type: i1$1.LocalizationService
  }], null);
})();

/**
 * Represents an insertion point for Windows created through the Window service.
 * The created Windows mount after this element. ([See example.](https://www.telerik.com/kendo-angular-ui/components/dialogs/api/windowservice#open))
 *
 * @example
 * ```html
 * <div kendoWindowContainer></div>
 * ```
 */
class WindowContainerDirective {
  constructor(container, service) {
    service.container = container;
  }
  static ɵfac = function WindowContainerDirective_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || WindowContainerDirective)(i0.ɵɵdirectiveInject(i0.ViewContainerRef), i0.ɵɵdirectiveInject(WindowContainerService));
  };
  static ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
    type: WindowContainerDirective,
    selectors: [["", "kendoWindowContainer", ""]]
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(WindowContainerDirective, [{
    type: Directive,
    args: [{
      selector: '[kendoWindowContainer]',
      standalone: true
    }]
  }], () => [{
    type: i0.ViewContainerRef
  }, {
    type: WindowContainerService
  }], null);
})();

/**
 * Represents the utility array that that contains all `Dialog`-related components and directives.
 *
 * Use `KENDO_DIALOG` to import all Dialog components and directives at once.
 *
 * @example
 * ```typescript
 * import { Component } from '@angular/core';
 * import { KENDO_DIALOG } from '@progress/kendo-angular-dialog';
 *
 * @Component({
 *   selector: 'my-dialog-app',
 *   standalone: true,
 *   imports: [KENDO_DIALOG],
 *   template: `...`
 * })
 * export class DialogAppComponent {}
 * ```
 */
const KENDO_DIALOG = [DialogComponent, DialogTitleBarComponent, DialogContainerDirective, DialogActionsComponent, CustomMessagesComponent];
/**
 * Represents the utility array that contains all `Window`-related components and directives.
 *
 * Use `KENDO_WINDOW` to import all Window components and directives at once.
 *
 * @example
 * ```typescript
 * import { Component } from '@angular/core';
 * import { KENDO_WINDOW } from '@progress/kendo-angular-dialog';
 *
 * @Component({
 *   selector: 'my-window-app',
 *   standalone: true,
 *   imports: [KENDO_WINDOW],
 *   template: `...`
 * })
 * export class WindowAppComponent {}
 * ```
 */
const KENDO_WINDOW = [WindowComponent, WindowCloseActionDirective, WindowMinimizeActionDirective, WindowMaximizeActionDirective, WindowRestoreActionDirective, WindowTitleBarComponent, WindowContainerDirective, DialogActionsComponent, CustomMessagesComponent];
/**
 * Represents the utility array that contains all `@progress/kendo-angular-dialog`-related components and directives.
 *
 * Use `KENDO_DIALOGS` to import all Dialog and Window components and directives at once.
 *
 * @example
 * ```typescript
 * import { Component } from '@angular/core';
 * import { KENDO_DIALOGS } from '@progress/kendo-angular-dialog';
 *
 * @Component({
 *   selector: 'my-app',
 *   standalone: true,
 *   imports: [KENDO_DIALOGS],
 *   template: `...`
 * })
 * export class AppComponent {}
 * ```
 */
const KENDO_DIALOGS = [...KENDO_DIALOG, ...KENDO_WINDOW];

//IMPORTANT: NgModule export kept for backwards compatibility
/**
 * Represents the [NgModule](link:site.data.urls.angular['ngmoduleapi']) definition for the Dialog component.
 *
 * Use the `DialogModule` to include all Dialog components and directives in your application.
 *
 * @example
 * ```typescript
 * import { NgModule } from '@angular/core';
 * import { BrowserModule } from '@angular/platform-browser';
 * import { DialogModule } from '@progress/kendo-angular-dialog';
 * import { AppComponent } from './app.component';
 *
 * @NgModule({
 *     bootstrap:    [AppComponent],
 *     declarations: [AppComponent],
 *     imports:      [BrowserModule, DialogModule]
 * })
 * export class AppModule {}
 * ```
 */
class DialogModule {
  static ɵfac = function DialogModule_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DialogModule)();
  };
  static ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
    type: DialogModule,
    imports: [DialogComponent, DialogTitleBarComponent, DialogContainerDirective, DialogActionsComponent, CustomMessagesComponent],
    exports: [DialogComponent, DialogTitleBarComponent, DialogContainerDirective, DialogActionsComponent, CustomMessagesComponent]
  });
  static ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
    providers: [DialogContainerService, DialogService, IconsService],
    imports: [DialogComponent, DialogTitleBarComponent, DialogActionsComponent]
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DialogModule, [{
    type: NgModule,
    args: [{
      exports: [...KENDO_DIALOG],
      imports: [...KENDO_DIALOG],
      providers: [DialogContainerService, DialogService, IconsService]
    }]
  }], null, null);
})();

//IMPORTANT: NgModule export kept for backwards compatibility
/**
 * Represents the [NgModule](link:site.data.urls.angular['ngmoduleapi']) definition for the Window component.
 *
 * Use the `WindowModule` to include all Window components and directives in your application.
 *
 * @example
 * ```typescript
 * import { NgModule } from '@angular/core';
 * import { BrowserModule } from '@angular/platform-browser';
 * import { WindowModule } from '@progress/kendo-angular-window';
 * import { AppComponent } from './app.component';
 *
 * @NgModule({
 *     bootstrap:    [AppComponent],
 *     declarations: [AppComponent],
 *     imports:      [BrowserModule, WindowModule]
 * })
 * export class AppModule {}
 * ```
 */
class WindowModule {
  static ɵfac = function WindowModule_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || WindowModule)();
  };
  static ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
    type: WindowModule,
    imports: [WindowComponent, WindowCloseActionDirective, WindowMinimizeActionDirective, WindowMaximizeActionDirective, WindowRestoreActionDirective, WindowTitleBarComponent, WindowContainerDirective, DialogActionsComponent, CustomMessagesComponent],
    exports: [WindowComponent, WindowCloseActionDirective, WindowMinimizeActionDirective, WindowMaximizeActionDirective, WindowRestoreActionDirective, WindowTitleBarComponent, WindowContainerDirective, DialogActionsComponent, CustomMessagesComponent]
  });
  static ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
    providers: [WindowContainerService, WindowService, IconsService],
    imports: [WindowComponent, WindowCloseActionDirective, WindowMinimizeActionDirective, WindowMaximizeActionDirective, WindowRestoreActionDirective, DialogActionsComponent]
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(WindowModule, [{
    type: NgModule,
    args: [{
      imports: [...KENDO_WINDOW],
      exports: [...KENDO_WINDOW],
      providers: [WindowContainerService, WindowService, IconsService]
    }]
  }], null, null);
})();

//IMPORTANT: NgModule export kept for backwards compatibility
/**
 * Represents the [NgModule](link:site.data.urls.angular['ngmoduleapi']) definition for the Dialogs components.
 *
 * Use the `DialogsModule` to include all Dialog and Window components and services in your application.
 *
 * @example
 * ```typescript
 * import { DialogsModule } from '@progress/kendo-angular-dialog';
 * import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
 * import { NgModule } from '@angular/core';
 * import { AppComponent } from './app.component';
 *
 * @NgModule({
 *     declarations: [AppComponent],
 *     imports:      [BrowserModule, DialogsModule],
 *     bootstrap:    [AppComponent]
 * })
 * export class AppModule {}
 *
 * ```
 */
class DialogsModule {
  static ɵfac = function DialogsModule_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DialogsModule)();
  };
  static ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
    type: DialogsModule,
    imports: [DialogComponent, DialogTitleBarComponent, DialogContainerDirective, DialogActionsComponent, CustomMessagesComponent, WindowComponent, WindowCloseActionDirective, WindowMinimizeActionDirective, WindowMaximizeActionDirective, WindowRestoreActionDirective, WindowTitleBarComponent, WindowContainerDirective, DialogActionsComponent, CustomMessagesComponent],
    exports: [DialogComponent, DialogTitleBarComponent, DialogContainerDirective, DialogActionsComponent, CustomMessagesComponent, WindowComponent, WindowCloseActionDirective, WindowMinimizeActionDirective, WindowMaximizeActionDirective, WindowRestoreActionDirective, WindowTitleBarComponent, WindowContainerDirective, DialogActionsComponent, CustomMessagesComponent]
  });
  static ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
    providers: [IconsService, DialogService, DialogContainerService, WindowService, WindowContainerService],
    imports: [DialogComponent, DialogTitleBarComponent, DialogActionsComponent, WindowComponent, WindowCloseActionDirective, WindowMinimizeActionDirective, WindowMaximizeActionDirective, WindowRestoreActionDirective, DialogActionsComponent]
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DialogsModule, [{
    type: NgModule,
    args: [{
      imports: [...KENDO_DIALOGS],
      exports: [...KENDO_DIALOGS],
      providers: [IconsService, DialogService, DialogContainerService, WindowService, WindowContainerService]
    }]
  }], null, null);
})();

/**
 * Generated bundle index. Do not edit.
 */

export { CustomMessagesComponent, DialogAction, DialogActionsComponent, DialogCloseResult, DialogComponent, DialogContainerDirective, DialogContainerService, DialogContentBase, DialogModule, DialogRef, DialogService, DialogSettings, DialogTitleBarComponent, DialogsModule, DragResizeService, KENDO_DIALOG, KENDO_DIALOGS, KENDO_WINDOW, LocalizedMessagesDirective, Messages, NavigationService, PreventableEvent, WindowCloseActionDirective, WindowCloseResult, WindowComponent, WindowContainerDirective, WindowContainerService, WindowMaximizeActionDirective, WindowMinimizeActionDirective, WindowModule, WindowRef, WindowRestoreActionDirective, WindowService, WindowSettings, WindowTitleBarComponent };